// @safetyhub/report-composer — 작성 페이지 위젯 트리 뮤테이션 (순수 함수)
//
// composerWidgetTree(ComposerWidget[])를 조작하는 순수 배열 함수. 노드의 templateOps에 대응하는
// 작성 페이지 저작 기반 — 미배치 풀→트리 배치(place), 트리 내 이동(move), 제거(remove),
// 수정(update), 그룹/디자인 위젯 팩토리.
//
// 결정: 2026-07-08-composer-writeform-editing-structure §5-2(semantic reorder).
// 저장은 caller가 `apply(t => ({ ...t, composerWidgetTree: opFn(t.composerWidgetTree ?? [], ...) }))`로 배선.

import {
  REGISTERED_FORM_WIDGETS_META,
  type BranchWidget,
  type BranchWidgetSlot,
  type ComposerWidget,
  type DesignWidget,
  type FormTableColumn,
  type FormTableFixedCell,
  type FormTableRow,
  type FormTableWidget,
  type GroupWidget,
  type InputWidget,
  type TableCellStyle,
} from '@safetyhub/shared';

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

/**
 * children 컨테이너 위젯(group·modalTrigger·repeat) 판별 — 트리 구조 순회·뮤테이션의 하강 대상.
 * repeat children(행 템플릿)은 값 스코프는 행 격리지만 **트리 구조로는 일반 children**이라
 * 저작(삽입/제거/이동/갱신)이 group과 동형으로 하강해야 한다(하강 누락 = 행 템플릿 편집 침묵 무효).
 *
 * ⚠️ branch는 제외한다 — children이 평탄 배열이 아니라 `branches[].children`(슬롯별)이라 이 판별에
 *    태울 수 없다. 각 순회 지점에서 `w.kind === 'branch'` 분기로 슬롯별 하강을 명시 추가한다.
 *
 * **컨테이너 kind 목록의 정본이다** — admin(작성 탭 Ctrl+A 형제 계산 등)도 kind 목록을 손으로
 * 복제하지 말고 이것을 import한다(새 컨테이너 kind 추가 시 한쪽만 갱신되면 조용히 죽는다).
 */
export function isChildrenContainer(
  w: ComposerWidget,
): w is Extract<ComposerWidget, { kind: 'group' | 'modalTrigger' | 'repeat' }> {
  return w.kind === 'group' || w.kind === 'modalTrigger' || w.kind === 'repeat';
}

/**
 * 위젯 subtree의 모든 id 수집(group·modalTrigger·repeat children + branch 슬롯 id/슬롯 children
 * 재귀). cycle 가드용 — 슬롯 id를 포함해야 branch를 자기 슬롯 안으로 이동하는 것을 막는다.
 */
function collectSubtreeIds(widget: ComposerWidget): Set<string> {
  const ids = new Set<string>();
  const walk = (w: ComposerWidget): void => {
    ids.add(w.id);
    if (isChildrenContainer(w)) for (const c of w.children) walk(c);
    else if (w.kind === 'branch')
      for (const slot of w.branches) {
        ids.add(slot.id); // 슬롯 id(트리 순회 부모 키)도 subtree 소유 — cycle 대상
        for (const c of slot.children) walk(c);
      }
  };
  walk(widget);
  return ids;
}

/**
 * 위젯을 트리에 삽입. parentId=null이면 root, 아니면 해당 group/modalTrigger/repeat의 children[index]
 * 또는 branch **슬롯**(parentId=슬롯 id)의 children[index]에. branch 위젯 id 자체는 children 컨테이너
 * 키가 아니다(직속 children 배열이 없음 — 슬롯 id만 삽입 대상). index는 대상 배열 길이로 clamp.
 * parentId를 못 찾으면 무변경(위젯 유실 방지 위해 root 말미 append).
 */
export function insertWidgetAt(
  tree: ComposerWidget[],
  widget: ComposerWidget,
  parentId: string | null,
  index: number,
): ComposerWidget[] {
  if (parentId == null) {
    const i = clamp(index, 0, tree.length);
    return [...tree.slice(0, i), widget, ...tree.slice(i)];
  }
  let inserted = false;
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] =>
    nodes.map((w) => {
      if (isChildrenContainer(w)) {
        if (w.id === parentId) {
          inserted = true;
          const i = clamp(index, 0, w.children.length);
          return { ...w, children: [...w.children.slice(0, i), widget, ...w.children.slice(i)] };
        }
        const children = walk(w.children);
        return children === w.children ? w : { ...w, children };
      }
      if (w.kind === 'branch') {
        let slotsChanged = false;
        const branches = w.branches.map((slot) => {
          if (slot.id === parentId) {
            inserted = true;
            slotsChanged = true;
            const i = clamp(index, 0, slot.children.length);
            return {
              ...slot,
              children: [...slot.children.slice(0, i), widget, ...slot.children.slice(i)],
            };
          }
          const children = walk(slot.children);
          if (children === slot.children) return slot;
          slotsChanged = true;
          return { ...slot, children };
        });
        return slotsChanged ? { ...w, branches } : w;
      }
      return w;
    });
  const next = walk(tree);
  // parentId를 못 찾으면 위젯 유실 대신 root 말미로.
  return inserted ? next : [...tree, widget];
}

/** 트리에서 id 위젯 제거. 제거된 위젯도 함께 반환(move의 재삽입용). 미발견 시 원본 트리 그대로. */
export function removeWidgetById(
  tree: ComposerWidget[],
  id: string,
): { tree: ComposerWidget[]; removed: ComposerWidget | undefined } {
  let removed: ComposerWidget | undefined;
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] => {
    let changed = false;
    const out: ComposerWidget[] = [];
    for (const w of nodes) {
      if (w.id === id) {
        removed = w;
        changed = true;
        continue;
      }
      if (isChildrenContainer(w)) {
        const children = walk(w.children);
        if (children !== w.children) {
          changed = true;
          out.push({ ...w, children });
          continue;
        }
      }
      if (w.kind === 'branch') {
        // branch 위젯 자체 제거는 위 `w.id === id`가 처리. 여기선 슬롯 children 하강 제거.
        let slotsChanged = false;
        const branches = w.branches.map((slot) => {
          const children = walk(slot.children);
          if (children === slot.children) return slot;
          slotsChanged = true;
          return { ...slot, children };
        });
        if (slotsChanged) {
          changed = true;
          out.push({ ...w, branches });
          continue;
        }
      }
      if (w.kind === 'formTable') {
        // 표 고정 셀 위젯 제거(캔버스 Delete) — 셀은 '입력(위젯 미선택)' 상태로 유지(패널 '빼기'와
        // 동일 규약). 파생 위젯은 placed 집합에서 빠져 자동 풀 복귀, treeOwned는 소멸(=삭제).
        let rowsChanged = false;
        const rows = w.rows.map((row) => {
          if (row.type !== 'fixed') return row;
          let cellsChanged = false;
          const cells = row.cells.map((cell): FormTableFixedCell => {
            if (!cell.widget || cell.widget.id !== id) return cell;
            removed = cell.widget;
            cellsChanged = true;
            return {
              columnId: cell.columnId,
              ...(cell.colSpan ? { colSpan: cell.colSpan } : {}),
              ...(cell.text !== undefined ? { text: cell.text } : {}),
            };
          });
          if (cellsChanged) rowsChanged = true;
          return cellsChanged ? { ...row, cells } : row;
        });
        if (rowsChanged) {
          changed = true;
          out.push({ ...w, rows });
          continue;
        }
      }
      out.push(w);
    }
    return changed ? out : nodes;
  };
  const next = walk(tree);
  return { tree: removed ? next : tree, removed };
}

/**
 * 위젯을 형제 배열 안에서 한 칸 이동(up/down). 부모(root 또는 group) 안에서만 스왑, 경계면 무변경.
 * 버튼 기반 reorder용 — parentId/index 계산 없이 id만으로.
 */
export function moveWidgetInSiblings(
  tree: ComposerWidget[],
  id: string,
  dir: 'up' | 'down',
): ComposerWidget[] {
  // applyToSiblingsOf(참조 변경 전파)로 임의 깊이에서 동작. (종전 자체 재귀는 hit 플래그를
  // `hit: false`로 하드코딩 반환해 2단계+ 중첩에서 스왑된 서브트리가 상위 계층에서 버려지는
  // 버그 — 2026-07-16 제보 "측정 장비명(3단 중첩) ↑ 무동작". 루트/1단계만 동작했음.)
  return applyToSiblingsOf(tree, id, (siblings) => {
    const idx = siblings.findIndex((w) => w.id === id);
    const to = dir === 'up' ? idx - 1 : idx + 1;
    if (to < 0 || to >= siblings.length) return siblings; // 경계 — 무변경
    const next = [...siblings];
    [next[idx], next[to]] = [next[to], next[idx]];
    return next;
  });
}

// ── 뷰별 순서 오버라이드(Phase 3) — base 배열은 불변, responsive[view].order만 갱신 ──

/** 위젯의 뷰별 order 병합(부분 델타). */
function withViewOrder(w: ComposerWidget, view: string, order: number): ComposerWidget {
  const resp = { ...(w.responsive ?? {}) };
  resp[view] = { ...(resp[view] ?? {}), order };
  return { ...w, responsive: resp };
}

/** 형제 배열의 뷰 표시 순서 — responsive[view].order ?? baseIndex(안정 정렬). */
export function orderSiblingsForView(
  siblings: ComposerWidget[],
  view: string,
): ComposerWidget[] {
  return siblings
    .map((w, i) => ({ w, key: w.responsive?.[view]?.order ?? i, i }))
    .sort((a, b) => a.key - b.key || a.i - b.i)
    .map((x) => x.w);
}

/**
 * 형제 배열에서 id를 그 뷰 표시순의 targetDisplayIndex로 옮기고, 형제 전원의
 * responsive[view].order를 새 표시순(0..n)으로 materialize. base 배열 순서는 유지.
 */
function reorderSiblingsForView(
  siblings: ComposerWidget[],
  id: string,
  targetDisplayIndex: number,
  view: string,
): ComposerWidget[] {
  const display = orderSiblingsForView(siblings, view);
  const from = display.findIndex((w) => w.id === id);
  if (from < 0) return siblings;
  const reordered = [...display];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(clamp(targetDisplayIndex, 0, reordered.length), 0, moved);
  const orderById = new Map(reordered.map((w, idx) => [w.id, idx]));
  return siblings.map((w) => withViewOrder(w, view, orderById.get(w.id) ?? 0));
}

/**
 * 트리에서 id가 속한 형제 배열(root / group·modalTrigger·repeat.children / branch 슬롯 children)에
 * fn 적용. branch 슬롯 children도 형제 배열로 취급해 그 안 reorder/스왑이 동작한다.
 */
function applyToSiblingsOf(
  tree: ComposerWidget[],
  id: string,
  fn: (siblings: ComposerWidget[]) => ComposerWidget[],
): ComposerWidget[] {
  if (tree.some((w) => w.id === id)) return fn(tree);
  let changed = false;
  const next = tree.map((w) => {
    if (isChildrenContainer(w)) {
      const children = applyToSiblingsOf(w.children, id, fn);
      if (children !== w.children) {
        changed = true;
        return { ...w, children };
      }
      return w;
    }
    if (w.kind === 'branch') {
      let slotsChanged = false;
      const branches = w.branches.map((slot) => {
        const children = applyToSiblingsOf(slot.children, id, fn);
        if (children === slot.children) return slot;
        slotsChanged = true;
        return { ...slot, children };
      });
      if (slotsChanged) {
        changed = true;
        return { ...w, branches };
      }
      return w;
    }
    return w;
  });
  return changed ? next : tree;
}

/** 뷰별 순서: id를 up/down 한 칸(그 뷰 표시순 기준). 경계면 무변경. */
export function moveWidgetInSiblingsForView(
  tree: ComposerWidget[],
  id: string,
  dir: 'up' | 'down',
  view: string,
): ComposerWidget[] {
  return applyToSiblingsOf(tree, id, (siblings) => {
    const display = orderSiblingsForView(siblings, view);
    const cur = display.findIndex((w) => w.id === id);
    if (cur < 0) return siblings;
    const to = dir === 'up' ? cur - 1 : cur + 1;
    if (to < 0 || to >= display.length) return siblings;
    return reorderSiblingsForView(siblings, id, to, view);
  });
}

/** 뷰별 순서: id를 그 뷰 표시순의 targetDisplayIndex로(dnd 같은 부모). */
export function moveWidgetToIndexForView(
  tree: ComposerWidget[],
  id: string,
  targetDisplayIndex: number,
  view: string,
): ComposerWidget[] {
  return applyToSiblingsOf(tree, id, (siblings) =>
    reorderSiblingsForView(siblings, id, targetDisplayIndex, view),
  );
}

/**
 * 위젯 하나에서 그 뷰의 `order` 키만 제거(다른 축·다른 뷰는 보존). 오버라이드가 없으면 **같은
 * 참조**를 돌려준다 — caller(applyToSiblingsOf)가 참조 비교로 "무변경"을 판정하기 때문.
 *
 * 정리 규약은 setGroupColumnsForView(admin)와 동형: `responsive[view]`가 비면 뷰 키를,
 * `responsive`가 비면 전체를 undefined로(빈 껍데기가 JSON에 남지 않게).
 */
function withoutViewOrder(w: ComposerWidget, view: string): ComposerWidget {
  const cur = w.responsive?.[view];
  if (!cur || cur.order === undefined) return w;
  const nextView = { ...cur };
  delete nextView.order;
  const resp = { ...(w.responsive ?? {}) };
  if (Object.keys(nextView).length) resp[view] = nextView;
  else delete resp[view];
  return { ...w, responsive: Object.keys(resp).length ? resp : undefined };
}

/** 트리에서 id가 속한 형제 배열을 읽기 전용으로 찾는다(applyToSiblingsOf의 read 짝). 없으면 null. */
function findSiblingsOf(tree: ComposerWidget[], id: string): ComposerWidget[] | null {
  if (tree.some((w) => w.id === id)) return tree;
  for (const w of tree) {
    if (isChildrenContainer(w)) {
      const found = findSiblingsOf(w.children, id);
      if (found) return found;
    } else if (w.kind === 'branch') {
      for (const slot of w.branches) {
        const found = findSiblingsOf(slot.children, id);
        if (found) return found;
      }
    }
  }
  return null;
}

/**
 * id가 속한 형제 집합에 그 뷰의 순서 오버라이드가 걸려 있는가 — 저작 UI가 "이 뷰에서 순서가
 * 조정돼 있음"을 표시하고 초기화 버튼을 노출할지 판정하는 read 축.
 *
 * ⚠️ **위젯 단위가 아니라 형제 집합 단위**다. reorderSiblingsForView가 한 번 옮길 때 형제 전원에
 * order를 박으므로(base 배열 불변), 이 위젯 자신에 order가 없어도 형제가 조정된 상태일 수 있다.
 */
export function hasSiblingViewOrder(
  tree: ComposerWidget[],
  id: string,
  view: string,
): boolean {
  const siblings = findSiblingsOf(tree, id);
  return !!siblings?.some((w) => w.responsive?.[view]?.order !== undefined);
}

/**
 * id가 속한 형제 **전원**에서 그 뷰의 order 오버라이드를 제거 → 그 뷰 표시순이 base 배열 순서로
 * 복귀. root 직계(부모 없음)·group/modalTrigger/repeat children·branch 슬롯 children 모두 대상.
 *
 * 집합 단위인 이유는 hasSiblingViewOrder 주석과 같다 — 한 위젯만 order를 지우면 그 위젯이
 * `order ?? baseIndex` fallback으로 엉뚱한 자리에 끼어 순서가 더 망가진다.
 */
export function clearSiblingViewOrder(
  tree: ComposerWidget[],
  id: string,
  view: string,
): ComposerWidget[] {
  return applyToSiblingsOf(tree, id, (siblings) => {
    let changed = false;
    const next = siblings.map((w) => {
      const cleaned = withoutViewOrder(w, view);
      if (cleaned !== w) changed = true;
      return cleaned;
    });
    return changed ? next : siblings;
  });
}

/**
 * 트리 내 위젯을 새 위치로 이동. cycle 가드(자기/후손 그룹으로 이동 불가).
 * index는 **제거 후** 대상 배열 기준(같은 부모 내 재정렬 시 앞 형제 제거로 인한 시프트는 caller 책임).
 */
export function moveWidget(
  tree: ComposerWidget[],
  id: string,
  parentId: string | null,
  index: number,
): ComposerWidget[] {
  const { tree: without, removed } = removeWidgetById(tree, id);
  if (!removed) return tree;
  if (parentId != null && collectSubtreeIds(removed).has(parentId)) return tree; // cycle
  return insertWidgetAt(without, removed, parentId, index);
}

/**
 * 입력 위젯이 body 바인딩 파생인지(=트리 소유 아님). 파생 input은 bindingKey가 양식 바인딩과 1:1이라
 * 복제 시 중복이 생겨 복제 대상에서 제외한다. 등록형(widget==='registered')·treeOwned는 트리 소유물
 * (파생 아님)이라 복제 가능 — composerWidgetSync.treeOwnedWidget과 동일 판정.
 *
 * **이 판정의 정본이다** — admin(nodeClipboard 등)도 손으로 재구현하지 말고 이것을 import한다.
 * 세 번째 트리소유 마커가 생겨도 한 곳만 고치면 되게(복제본은 조용히 오분류된다).
 */
export function isDerivedInput(w: InputWidget): boolean {
  return w.widget !== 'registered' && !w.treeOwned;
}

/**
 * 위젯 목록에서 복제 시 **제외될** 파생(비-트리소유) input 수 — clone 규칙(cloneWidgetsForInsert/
 * cloneWidgetDeep)과 동일 walk: 최상위·group children의 파생 input, formTable 고정 셀의 파생 셀 위젯.
 * modalTrigger children은 모달 스코프라 제외 대상이 아니므로 세지 않는다.
 * 호출처(admin 붙여넣기/복제)가 0보다 크면 "제외됨" 안내를 띄워 조용한 유실을 막는다(2026-07-16 —
 * 파생 textarea가 든 섹션 붙여넣기에서 위젯이 소리 없이 빠져 혼란 유발 제보).
 */
export function countDerivedInputsExcludedOnClone(widgets: ComposerWidget[]): number {
  let n = 0;
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') {
        if (isDerivedInput(w)) n += 1;
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        // 투명 컨테이너(부모 스코프 키) — 슬롯 children의 파생 input을 group처럼 카운트.
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget && isDerivedInput(cell.widget)) n += 1;
        }
      }
      // modalTrigger: children은 모달 스코프 보존(제외 안 됨) — 카운트 대상 아님.
      // repeat: children은 행 스코프(bindingKey=행 필드 상대키)라 body 파생 개념이 없음 — 카운트 대상 아님.
    }
  };
  walk(widgets);
  return n;
}

/**
 * 프리셋 저장·삽입에서 **제외될** 파생 input 수 — clonePresetWalk의 제외 규칙과 같은 경계.
 * 복제/붙여넣기용 countDerivedInputsExcludedOnClone과 **루트 취급만 다르다**: clonePresetWalk는
 * 루트가 input이면 clonePresetInput으로 그대로 클론하므로(제외 안 함) 0을 돌려준다. 그 경우
 * 재발급된 bindingKey가 대상 양식에 없으면 orphan이 되는데, 그건 "제외"가 아니라 미해소라 축이 다르다.
 * 호출처(admin 프리셋 저장 모달·삽입 안내)가 0보다 크면 알려 조용한 유실을 막는다.
 */
export function countPresetExcludedDerivedInputs(root: ComposerWidget): number {
  if (root.kind === 'input') return 0;
  return countDerivedInputsExcludedOnClone([root]);
}

/** 트리 소유 입력 위젯을 클론 — 새 id + '-copy' 유니크 bindingKey(taken 누적). 마커(treeOwned/registered)는 유지. */
function cloneTreeOwnedInput(
  w: InputWidget,
  taken: Set<string>,
  newId: () => string,
): InputWidget {
  const base = `${w.bindingKey}-copy`;
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}-${n++}`;
  taken.add(key); // 같은 복제 안 다음 대상에서 키 재배정 방지
  return { ...w, id: newId(), bindingKey: key };
}

/**
 * 위젯 subtree 딥 클론 — 모든 위젯 id를 newId()로 재발급하고, 트리 소유 입력 위젯의 bindingKey는
 * taken 기준 '-copy' 유니크 재발급한다. body 바인딩 파생 입력(비-트리소유)은 제외:
 *  - group children: 스킵 / formTable 고정 셀: widget만 제거(text/정렬 등 나머지 유지, 빈 입력 셀).
 * formTable 행/열 id는 위젯 내부 스코프(셀은 columnId 매칭)라 유지 — 재발급 불필요.
 */
function cloneWidgetDeep(
  widget: ComposerWidget,
  taken: Set<string>,
  newId: () => string,
): ComposerWidget {
  if (widget.kind === 'input') {
    return cloneTreeOwnedInput(widget, taken, newId);
  }
  if (widget.kind === 'group') {
    const children: ComposerWidget[] = [];
    for (const c of widget.children) {
      if (c.kind === 'input' && isDerivedInput(c)) continue; // 파생 자식 제외(bindingKey 중복 방지)
      children.push(cloneWidgetDeep(c, taken, newId));
    }
    return { ...widget, id: newId(), children };
  }
  if (widget.kind === 'branch') {
    // 투명 컨테이너 — 슬롯 children은 부모 스코프 키라 group 규칙(파생 제외 + treeOwned '-copy').
    // 슬롯 id는 트리 유일 부모 키라 위젯 id와 함께 재발급(repeat/modalTrigger의 키 보존과 다름).
    const branches = widget.branches.map((slot) => {
      const children: ComposerWidget[] = [];
      for (const c of slot.children) {
        if (c.kind === 'input' && isDerivedInput(c)) continue; // 파생 자식 제외(bindingKey 중복 방지)
        children.push(cloneWidgetDeep(c, taken, newId));
      }
      return { ...slot, id: newId(), children };
    });
    return { ...widget, id: newId(), branches };
  }
  if (widget.kind === 'formTable') {
    const rows = widget.rows.map((row): FormTableRow => {
      if (row.type !== 'fixed') return { ...row }; // 반복 행: 구조(sourceKey/cellFields) 그대로
      const cells = row.cells.map((cell): FormTableFixedCell => {
        if (!cell.widget) return { ...cell };
        if (isDerivedInput(cell.widget)) {
          const stripped: FormTableFixedCell = { ...cell };
          delete stripped.widget; // 파생 셀 위젯 제외 — 빈 입력 셀로 남김
          return stripped;
        }
        return { ...cell, widget: cloneTreeOwnedInput(cell.widget, taken, newId) };
      });
      return { ...row, cells };
    });
    return { ...widget, id: newId(), rows };
  }
  if (widget.kind === 'modalTrigger') {
    // 위젯 자체 bindingKey는 **본문 키**(모달 UI 상태 저장)라 복제 시 -copy로 유니크 재발급(중복 저장 방지).
    // children bindingKey는 **모달 스코프**(본문과 격리)라 그대로 둔다 — -copy로 바꾸면 commit.from 참조가
    // 끊긴다. children id만 재발급해 트리 유일성 확보(파생 대조 대상 아님).
    const base = `${widget.bindingKey}-copy`;
    let key = base;
    let n = 2;
    while (taken.has(key)) key = `${base}-${n++}`;
    taken.add(key);
    return {
      ...widget,
      id: newId(),
      bindingKey: key,
      children: widget.children.map((c) => reIdWidgetSubtree(c, newId)),
    };
  }
  if (widget.kind === 'repeat') {
    // 행 템플릿 children의 bindingKey는 **행 필드 상대키**(over 데이터소스 def.fields와 1:1) —
    // '-copy' 재발급하면 필드 매핑이 끊기므로 보존하고 id만 재발급(modalTrigger children 규약 동형).
    // repeat 자신은 bindingKey 없음(over는 데이터소스 참조라 그대로).
    return {
      ...widget,
      id: newId(),
      children: widget.children.map((c) => reIdWidgetSubtree(c, newId)),
    };
  }
  // design(separator/heading/spacer)
  return { ...widget, id: newId() };
}

/**
 * 위젯 subtree의 모든 위젯 id를 newId()로 재발급(bindingKey·구조는 보존). modalTrigger children 클론
 * 전용 — 모달 스코프는 본문과 격리라 bindingKey 재발급 없이 id 유일성만 확보한다.
 * @param idMap 지정 시 old id→new id를 기록(프리셋 클론의 id 참조 재매핑용 — 기존 호출처는 미지정).
 */
function reIdWidgetSubtree(
  widget: ComposerWidget,
  newId: () => string,
  idMap?: Map<string, string>,
): ComposerWidget {
  const issueId = (oldId: string): string => {
    const id = newId();
    idMap?.set(oldId, id);
    return id;
  };
  const issue = (w: ComposerWidget): string => issueId(w.id);
  if (widget.kind === 'group' || widget.kind === 'modalTrigger' || widget.kind === 'repeat') {
    // repeat children도 하강 — 행 상대 bindingKey는 보존, id만 재발급(모달 children 동형).
    return { ...widget, id: issue(widget), children: widget.children.map((c) => reIdWidgetSubtree(c, newId, idMap)) };
  }
  if (widget.kind === 'branch') {
    // 슬롯 id도 트리 유일 부모 키라 재발급(idMap 기록). children bindingKey는 이 경로(모달/행 스코프
    // 안 branch)에선 보존, id만 재발급 — reIdWidgetSubtree 규약 동형.
    const branches = widget.branches.map((slot) => ({
      ...slot,
      id: issueId(slot.id),
      children: slot.children.map((c) => reIdWidgetSubtree(c, newId, idMap)),
    }));
    return { ...widget, id: issue(widget), branches };
  }
  if (widget.kind === 'formTable') {
    const rows = widget.rows.map((row): FormTableRow => {
      if (row.type !== 'fixed') return { ...row };
      const cells = row.cells.map((cell): FormTableFixedCell =>
        cell.widget ? { ...cell, widget: { ...cell.widget, id: issue(cell.widget) } } : { ...cell },
      );
      return { ...row, cells };
    });
    return { ...widget, id: issue(widget), rows };
  }
  return { ...widget, id: issue(widget) };
}

// ─────────────────────────────────────────────────────────────
// 위젯 프리셋 v1 — 삽입 클론 + 내부 참조 재매핑 / 저장 정규화
// (결정: 2026-07-17-composer-widget-preset-and-registered-widget-path — "위젯 프리셋"·"v1 범위 확정")
//
// 프리셋 가치의 핵심 = 묶음 내부 상대 참조(writes·visibleWhen·enabledWhen·labelSuffixFrom·
// actionItem.opensWidgetId·modalTrigger commit 착지·계산 셀 입력)가 삽입 시 id/bindingKey
// 재발급과 함께 자동 재매핑되는 것. 묶음 밖 대상(배열 일괄 등)은 원값 보존 + 미해소 목록으로
// 보고해 삽입 후 저작자가 지정한다.
//
// 참조 축 전수(2026-07-19 shared 타입 doc + rc 렌더 소비처 grep으로 확정):
//  - bindingKey 축(작성 스코프 values 참조 — 본문 스코프에서만 재매핑):
//    visibleWhen / enabledWhen (ComposerWidgetBase — 전 위젯) ·
//    labelSuffixFrom / writes[].target (InputWidget — 셀 위젯 포함) ·
//    modalTrigger commit[].to + purpose.commit[].to (본문 착지 키 — from은 모달 스코프라 보존) ·
//    formTable 고정 셀 compute.inputs[].binding.bindingKey (writeValues 스코프 실시간 계산 —
//    composerWidgetTreeRender가 computeCellCompute(cell.compute, values)로 소비).
//  - 위젯 id 축(트리 전역 유일 — 모달 스코프 안에서도 재매핑): actionItem.opensWidgetId.
//  - 무변(카탈로그·데이터소스·모달 내부 계약): prefillFrom / optionsFrom / optionsSource(s) /
//    sourceFieldMaps / writeBack / submitAction / repeat 행 sourceKey·cellFields / writes[].source.
// ─────────────────────────────────────────────────────────────

/** 프리셋 내부 참조 재매핑 축 어휘 — 미해소 보고(PresetUnresolvedRef.axis)에 사용. */
export type PresetRefAxis =
  | 'visibleWhen'
  | 'enabledWhen'
  | 'labelSuffixFrom'
  | 'writes.target'
  | 'actionItem.opensWidgetId'
  | 'commit.to'
  | 'compute.input';

/**
 * 프리셋 삽입 시 재매핑하지 못한(=묶음 밖 대상) 참조 1건 — 원값은 보존되고 이 목록으로 보고돼
 * 삽입 UI가 저작자에게 지정을 안내한다(배열 일괄 writes 등).
 */
export interface PresetUnresolvedRef {
  /** 참조를 보유한 위젯의 **새(재발급) id**. 계산 셀은 위젯이 없어 소유 formTable 위젯 id. */
  widgetId: string;
  axis: PresetRefAxis;
  /** 보존된 원래 참조 값(bindingKey / 배열 일괄 target / 위젯 id) */
  value: string;
}

/**
 * 프리셋 삽입용 bindingKey 유니크 재발급 — '-copy' 관례와 달리 **원 키가 비어 있으면 그대로 유지**
 * (프리셋 원본은 대상 트리 밖이라 무충돌이 보통 — 첫 삽입은 저작 시 키가 그대로 살아 가독 유지),
 * 충돌 시에만 `-2`, `-3`… 숫자 접미로 회피한다. taken에 배정 키를 누적해 같은 삽입 안 상호 충돌 방지.
 */
function presetUniqueKey(base: string, taken: Set<string>): string {
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}-${n++}`;
  taken.add(key);
  return key;
}

/** 프리셋 클론 재매핑 컨텍스트 — 클론 pass가 수집한 old→new 맵 + 미해소 누적. */
interface PresetRemapCtx {
  /** old bindingKey → new bindingKey (본문 스코프 위젯만 — 모달 children 키는 보존이라 미수집) */
  keyMap: Map<string, string>;
  /** old widget id → new widget id (모달 children 포함 전 위젯) */
  idMap: Map<string, string>;
  unresolved: PresetUnresolvedRef[];
}

/**
 * 프리셋 클론 walk 옵션.
 *
 * `keepDerivedInputs` — 파생(비-트리소유) input을 **제외하지 않고 함께 클론**한다(기본 false = 프리셋 규약).
 * 프리셋 삽입은 대응 body 노드가 대상 템플릿에 없으므로 파생 input을 제외하는 것이 정본이지만,
 * **위젯 클립보드 붙여넣기**(admin nodeClipboard P3)는 파생 위젯의 근거 body 노드를 payload에 동봉해
 * 함께 이식하므로 파생 input도 살려야 한다(제외하면 복사 때 모은 노드만 붙고 위젯이 사라진다).
 * 이때 파생 input의 bindingKey도 presetUniqueKey로 재발급되며, 호출처가 그 매핑(bindingKeyMap)으로
 * 동봉 노드의 binding.bindingKey를 같이 갱신(lockstep)해 파생 관계를 유지한다.
 */
export interface PresetCloneOptions {
  keepDerivedInputs?: boolean;
}

/** 입력 위젯 프리셋 클론 — 새 id + presetUniqueKey 재발급, old→new 맵 기록. 마커(treeOwned/registered) 유지. */
function clonePresetInput(
  w: InputWidget,
  taken: Set<string>,
  newId: () => string,
  idMap: Map<string, string>,
  keyMap: Map<string, string>,
): InputWidget {
  const id = newId();
  idMap.set(w.id, id);
  const key = presetUniqueKey(w.bindingKey, taken);
  keyMap.set(w.bindingKey, key);
  return { ...w, id, bindingKey: key };
}

/**
 * 프리셋 subtree 구조 클론(1차 pass) — cloneWidgetDeep과 동일 walk·제외 규칙(파생 input:
 * group children 스킵 / formTable 고정 셀 widget 스트립 — bindingKey 중복 방지)에 old→new
 * id/bindingKey 맵 수집을 더한 것. modalTrigger children은 모달 스코프 보존 규약 그대로
 * (bindingKey 무변, id만 재발급 — cloneWidgetDeep 342행 주석 규약과 일관) id 맵에만 기록한다.
 */
function clonePresetWalk(
  widget: ComposerWidget,
  taken: Set<string>,
  newId: () => string,
  idMap: Map<string, string>,
  keyMap: Map<string, string>,
  opts: PresetCloneOptions = {},
): ComposerWidget {
  const keepDerived = opts.keepDerivedInputs === true;
  if (widget.kind === 'input') {
    return clonePresetInput(widget, taken, newId, idMap, keyMap);
  }
  if (widget.kind === 'group') {
    const children: ComposerWidget[] = [];
    for (const c of widget.children) {
      if (!keepDerived && c.kind === 'input' && isDerivedInput(c)) continue; // 파생 자식 제외(cloneWidgetDeep 패리티)
      children.push(clonePresetWalk(c, taken, newId, idMap, keyMap, opts));
    }
    const id = newId();
    idMap.set(widget.id, id);
    return { ...widget, id, children };
  }
  if (widget.kind === 'branch') {
    // 투명 컨테이너 — 슬롯 children은 부모(본문) 스코프라 group 동형(bindingKey 재발급 = clonePresetWalk
    // 하강, 파생 제외). 슬롯 id는 트리 유일 부모 키라 재발급하고 idMap에 기록(canon이 상쇄 토큰화).
    const branches = widget.branches.map((slot) => {
      const children: ComposerWidget[] = [];
      for (const c of slot.children) {
        if (!keepDerived && c.kind === 'input' && isDerivedInput(c)) continue; // 파생 자식 제외(cloneWidgetDeep 패리티)
        children.push(clonePresetWalk(c, taken, newId, idMap, keyMap, opts));
      }
      const slotId = newId();
      idMap.set(slot.id, slotId);
      return { ...slot, id: slotId, children };
    });
    const id = newId();
    idMap.set(widget.id, id);
    return { ...widget, id, branches };
  }
  if (widget.kind === 'formTable') {
    const rows = widget.rows.map((row): FormTableRow => {
      if (row.type !== 'fixed') return { ...row }; // 반복 행: 구조(sourceKey/cellFields) 그대로
      const cells = row.cells.map((cell): FormTableFixedCell => {
        if (!cell.widget) return { ...cell };
        if (!keepDerived && isDerivedInput(cell.widget)) {
          const stripped: FormTableFixedCell = { ...cell };
          delete stripped.widget; // 파생 셀 위젯 제외 — 빈 입력 셀로 남김(cloneWidgetDeep 패리티)
          return stripped;
        }
        return { ...cell, widget: clonePresetInput(cell.widget, taken, newId, idMap, keyMap) };
      });
      return { ...row, cells };
    });
    const id = newId();
    idMap.set(widget.id, id);
    return { ...widget, id, rows };
  }
  if (widget.kind === 'modalTrigger') {
    // 위젯 자체 bindingKey는 본문 키(모달 UI 상태 저장)라 재발급+키 맵 기록. children bindingKey는
    // 모달 스코프(본문과 격리)라 보존 — id만 재발급하되 idMap에 기록(모달 안 콤보 actionItem이
    // 중첩 modalTrigger를 id로 참조할 수 있어 id 축 재매핑에 필요).
    const id = newId();
    idMap.set(widget.id, id);
    const key = presetUniqueKey(widget.bindingKey, taken);
    keyMap.set(widget.bindingKey, key);
    return {
      ...widget,
      id,
      bindingKey: key,
      children: widget.children.map((c) => reIdWidgetSubtree(c, newId, idMap)),
    };
  }
  if (widget.kind === 'repeat') {
    // 행 스코프 격리(모달 동형): children bindingKey는 행 필드 상대키라 보존, id만 재발급하되
    // idMap에 기록(행 안 콤보 actionItem→modalTrigger id 참조의 id 축 재매핑에 필요).
    const id = newId();
    idMap.set(widget.id, id);
    return {
      ...widget,
      id,
      children: widget.children.map((c) => reIdWidgetSubtree(c, newId, idMap)),
    };
  }
  // design(separator/heading/spacer)
  const id = newId();
  idMap.set(widget.id, id);
  return { ...widget, id };
}

/** bindingKey 축 재매핑 — 맵에 있으면 치환, 없으면(묶음 밖 대상) 원값 보존 + 미해소 기록. */
function remapPresetKey(
  ctx: PresetRemapCtx,
  ownerId: string,
  axis: PresetRefAxis,
  value: string,
): string {
  const mapped = ctx.keyMap.get(value);
  if (mapped !== undefined) return mapped;
  ctx.unresolved.push({ widgetId: ownerId, axis, value });
  return value;
}

/** 위젯 id 축 재매핑 — 맵에 있으면 치환, 없으면(묶음 밖 위젯 참조) 원값 보존 + 미해소 기록. */
function remapPresetId(
  ctx: PresetRemapCtx,
  ownerId: string,
  axis: PresetRefAxis,
  value: string,
): string {
  const mapped = ctx.idMap.get(value);
  if (mapped !== undefined) return mapped;
  ctx.unresolved.push({ widgetId: ownerId, axis, value });
  return value;
}

/** 배열 일괄 writes target의 구분자(`defKey[].field.path`) — parseBatchWriteTarget만 참조. */
const BATCH_WRITE_TARGET_SENTINEL = '[].';

/**
 * writes[].target의 **배열 일괄 형식**(`defKey[].field.path`) 판별·해체 — 이 문법의 정본이다.
 *
 * 생산(재매핑 시 보존+보고)과 소비(admin 붙여넣기 리포트의 미해소 재검증이 defKey를 데이터셋 키로
 * 대조)가 각자 `'[].'`를 파싱하면 문법 변경 시 소비측이 조용히 -1로 떨어져 **모든 일괄 writes가
 * 영구 오탐**이 된다 — 양쪽 모두 이 함수를 쓴다.
 *
 * @returns 일괄 형식이면 `{ defKey }`(sentinel 앞부분), 스칼라 바인딩 키면 `null`.
 */
export function parseBatchWriteTarget(target: string): { defKey: string } | null {
  const at = target.indexOf(BATCH_WRITE_TARGET_SENTINEL);
  if (at < 0) return null;
  return { defKey: target.slice(0, at) };
}

/** writes[].target 재매핑 — 배열 일괄(`defKey[].field.path`)은 묶음 밖(데이터셋) 참조라 항상 보존+보고. */
function remapPresetWriteTarget(ctx: PresetRemapCtx, ownerId: string, target: string): string {
  if (parseBatchWriteTarget(target)) {
    ctx.unresolved.push({ widgetId: ownerId, axis: 'writes.target', value: target });
    return target;
  }
  return remapPresetKey(ctx, ownerId, 'writes.target', target);
}

/**
 * 프리셋 내부 참조 재매핑(2차 pass) — clonePresetWalk가 만든 **클론 위젯 객체를 제자리 갱신**한다
 * (클론 pass가 전 위젯 객체를 새로 만들었으므로 원본 무접촉 — 공유 참조인 중첩 객체(writes 배열·
 * actionItem·commit·compute 등)는 치환 시 새 객체로 교체해 원본 오염을 막는다).
 *
 * 스코프 규칙: bindingKey 축은 **본문 스코프에서만** 재매핑·보고(모달 children의 키 참조는 모달
 * 스코프 namespace라 키 보존과 함께 그대로 유효 — 재매핑도 보고도 하지 않음). 위젯 id 축
 * (actionItem.opensWidgetId)은 id가 트리 전역 유일이라 모달 스코프 안에서도 재매핑한다.
 */
function remapPresetRefsInPlace(
  w: ComposerWidget,
  ctx: PresetRemapCtx,
  inModalScope: boolean,
): void {
  if (w.kind === 'input' && w.actionItem) {
    const v = remapPresetId(ctx, w.id, 'actionItem.opensWidgetId', w.actionItem.opensWidgetId);
    if (v !== w.actionItem.opensWidgetId) w.actionItem = { ...w.actionItem, opensWidgetId: v };
  }
  if (!inModalScope) {
    if (w.visibleWhen !== undefined) {
      w.visibleWhen = remapPresetKey(ctx, w.id, 'visibleWhen', w.visibleWhen);
    }
    if (w.enabledWhen !== undefined) {
      w.enabledWhen = remapPresetKey(ctx, w.id, 'enabledWhen', w.enabledWhen);
    }
    if (w.kind === 'input') {
      if (w.labelSuffixFrom !== undefined) {
        w.labelSuffixFrom = remapPresetKey(ctx, w.id, 'labelSuffixFrom', w.labelSuffixFrom);
      }
      if (w.writes && w.writes.length > 0) {
        w.writes = w.writes.map((wr) => ({
          ...wr,
          target: remapPresetWriteTarget(ctx, w.id, wr.target),
        }));
      }
    }
    if (w.kind === 'modalTrigger') {
      // commit(deprecated read fallback)과 purpose.commit(정본)은 admin이 dual-write하는 동일 값 —
      // 둘 다 재매핑(중복 미해소 보고는 clonePresetSubtreeForInsert가 dedupe).
      if (w.commit && w.commit.length > 0) {
        w.commit = w.commit.map((m) => ({ ...m, to: remapPresetKey(ctx, w.id, 'commit.to', m.to) }));
      }
      if (w.purpose && w.purpose.commit.length > 0) {
        w.purpose = {
          ...w.purpose,
          commit: w.purpose.commit.map((m) => ({
            ...m,
            to: remapPresetKey(ctx, w.id, 'commit.to', m.to),
          })),
        };
      }
    }
  }
  if (w.kind === 'group') {
    for (const c of w.children) remapPresetRefsInPlace(c, ctx, inModalScope);
  } else if (w.kind === 'modalTrigger') {
    for (const c of w.children) remapPresetRefsInPlace(c, ctx, true);
  } else if (w.kind === 'repeat') {
    // 행 스코프 격리 — bindingKey 축(visibleWhen/writes 등)은 행 필드 상대키라 재매핑·보고 제외
    // (모달 스코프와 동일 취급). 위젯 id 축(actionItem)은 스코프 무관 재매핑(remapPresetRefsInPlace
    // 상단 분기가 처리).
    for (const c of w.children) remapPresetRefsInPlace(c, ctx, true);
  } else if (w.kind === 'branch') {
    // 투명 컨테이너 — 슬롯 children은 부모 스코프라 inModalScope를 **그대로** 하강(group 동형).
    // 슬롯 자체엔 참조 축 없음(id/label/condition/children) — 슬롯 children만 순회.
    for (const slot of w.branches) for (const c of slot.children) remapPresetRefsInPlace(c, ctx, inModalScope);
  } else if (w.kind === 'formTable') {
    for (const row of w.rows) {
      if (row.type !== 'fixed') continue;
      for (let i = 0; i < row.cells.length; i += 1) {
        const cell = row.cells[i];
        if (cell.widget) remapPresetRefsInPlace(cell.widget, ctx, inModalScope);
        if (!inModalScope && cell.compute) {
          // 계산 셀 입력 — 같은 작성 스코프(writeValues) bindingKey 실시간 참조(상수 입력은 무변).
          row.cells[i] = {
            ...cell,
            compute: {
              ...cell.compute,
              inputs: cell.compute.inputs.map((inp) =>
                inp.binding
                  ? {
                      ...inp,
                      binding: {
                        ...inp.binding,
                        bindingKey: remapPresetKey(ctx, w.id, 'compute.input', inp.binding.bindingKey),
                      },
                    }
                  : inp,
              ),
            },
          };
        }
      }
    }
  }
}

/**
 * 프리셋 subtree를 삽입용으로 클론 — 전 위젯 id 재발급 + 본문 스코프 bindingKey를 taken 기준
 * 유니크 재발급(presetUniqueKey — 무충돌이면 원 키 유지)하고, 묶음 **내부** 상대 참조
 * (writes[].target·visibleWhen·enabledWhen·labelSuffixFrom·actionItem.opensWidgetId·
 * modalTrigger commit 본문 착지·계산 셀 입력)를 새 id/키로 자동 재매핑한다. 묶음 밖 대상
 * (배열 일괄 writes, 프리셋 밖 위젯·데이터 키 참조)은 원값 보존 + unresolvedRefs로 보고
 * (삽입 UI가 저작자에게 지정 안내). 파생(비-트리소유) input 제외와 modalTrigger children
 * 모달 스코프 키 보존은 cloneWidgetDeep 규약과 동일. 입력(root)은 불변.
 *
 * presetRef 스탬프는 하지 않는다 — 삽입 caller가 프리셋 {presetId, version}을 알고 루트에 스탬프.
 *
 * @param takenBindingKeys 대상 트리에서 이미 사용 중인 bindingKey 집합(트리+미배치+양식 —
 *   duplicateComposerWidget과 동일 계약). 배정 키를 여기 누적한다.
 * @param newId 위젯 id 생성기(테스트 결정성 위해 주입).
 * @param opts keepDerivedInputs — 파생 input을 제외하지 않고 함께 클론(위젯 클립보드 붙여넣기 전용,
 *   PresetCloneOptions 주석 참조). 미지정이면 기존 프리셋 규약(제외) 그대로.
 * @returns widget/unresolvedRefs + bindingKeyMap(old→new 본문 스코프 키 매핑) — 호출처가 함께 이식되는
 *   body 노드의 binding.bindingKey를 같은 매핑으로 갱신(lockstep)하는 데 쓴다. 재발급이 없었으면
 *   해당 키는 old===new로 담긴다(비었으면 재발급 대상 키 자체가 없었다는 뜻).
 */
export function clonePresetSubtreeForInsert(
  root: ComposerWidget,
  takenBindingKeys: Set<string>,
  newId: () => string,
  opts?: PresetCloneOptions,
): {
  widget: ComposerWidget;
  unresolvedRefs: PresetUnresolvedRef[];
  bindingKeyMap: Map<string, string>;
} {
  const idMap = new Map<string, string>();
  const keyMap = new Map<string, string>();
  const widget = clonePresetWalk(root, takenBindingKeys, newId, idMap, keyMap, opts);
  const ctx: PresetRemapCtx = { keyMap, idMap, unresolved: [] };
  remapPresetRefsInPlace(widget, ctx, false);
  // 중복 제거 — modalTrigger commit + purpose.commit dual-write가 같은 미해소를 2회 기록하는 것 정리.
  const seen = new Set<string>();
  const unresolvedRefs = ctx.unresolved.filter((u) => {
    const k = JSON.stringify([u.widgetId, u.axis, u.value]);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return { widget, unresolvedRefs, bindingKeyMap: keyMap };
}

/**
 * 프리셋 저장용 subtree 정규화 — 루트의 presetRef(출처 메타)를 제거한다. 배치본을 "프리셋으로 저장"
 * 하는 fork에서 새 프리셋은 새 정체성이므로 루트 출처를 지운다(결정: 프리셋 소유·격리 모델 —
 * "admin 것 수정" 경로 = 복사 모델). **중첩 하위의 presetRef는 정보 보존으로 유지**(그 서브트리가
 * 과거 어떤 프리셋에서 왔는지 기록일 뿐 버전 판정 대상 아님). 루트에 presetRef가 없으면 입력 그대로.
 */
export function normalizeSubtreeForPresetSave(root: ComposerWidget): ComposerWidget {
  if (!root.presetRef) return root;
  const next = { ...root };
  delete next.presetRef;
  return next;
}

// ─────────────────────────────────────────────────────────────
// 프리셋 인스턴스 로컬 수정 감지 — 배치 버전 스냅샷 ↔ 현재 서브트리 동등 판정
// (버전 적용 전 "로컬 수정 있음" 경고 게이트. clonePresetSubtreeForInsert의 id/bindingKey 재발급과
//  내부 참조 재매핑을 상쇄하는 canonical 정규화로 비교 — 클론 직후(무수정) 인스턴스는 배치 당시
//  버전 스냅샷과 canonical 동등이 된다. 정규화 규약은 clonePresetWalk/remapPresetRefsInPlace의
//  스코프 규칙을 그대로 반영: 위젯 id는 전 위젯 최초 등장 순 토큰, bindingKey는 **본문 스코프
//  선언 키만** 최초 등장 순 토큰(모달 children 키는 클론이 보존하므로 원값 유지=양쪽 리터럴 일치),
//  참조 축도 같은 맵으로 치환하되 묶음 밖(맵 밖)·모달 스코프 규칙은 clone과 동일. presetRef 출처
//  메타는 구조 아니므로 제외(루트 스탬프 유무가 판정을 흔들지 않게).)
// ─────────────────────────────────────────────────────────────

/** canonical 정규화 맵 — 위젯 id / 본문 스코프 bindingKey를 최초 등장 순서 토큰으로 치환. */
interface PresetCanonMaps {
  idMap: Map<string, string>;
  keyMap: Map<string, string>;
}

/** 1차 pass — 트리를 결정적 순회하며 위젯 id/본문 bindingKey에 canonical 토큰 배정(collect). */
function collectPresetCanonMaps(w: ComposerWidget, inModal: boolean, maps: PresetCanonMaps): void {
  if (!maps.idMap.has(w.id)) maps.idMap.set(w.id, `w${maps.idMap.size}`);
  if (
    !inModal &&
    (w.kind === 'input' || w.kind === 'modalTrigger') &&
    !maps.keyMap.has(w.bindingKey)
  ) {
    maps.keyMap.set(w.bindingKey, `k${maps.keyMap.size}`);
  }
  if (w.kind === 'group') {
    for (const c of w.children) collectPresetCanonMaps(c, inModal, maps);
  } else if (w.kind === 'modalTrigger') {
    for (const c of w.children) collectPresetCanonMaps(c, true, maps);
  } else if (w.kind === 'repeat') {
    // 행 스코프 = 키 보존 스코프(클론 규약 동형) — inModal=true로 하강해 키를 리터럴 비교.
    for (const c of w.children) collectPresetCanonMaps(c, true, maps);
  } else if (w.kind === 'branch') {
    // 투명 컨테이너 — 슬롯 id는 idMap 토큰화(clone 재발급 상쇄), children은 inModal **그대로** 하강.
    for (const slot of w.branches) {
      if (!maps.idMap.has(slot.id)) maps.idMap.set(slot.id, `w${maps.idMap.size}`);
      for (const c of slot.children) collectPresetCanonMaps(c, inModal, maps);
    }
  } else if (w.kind === 'formTable') {
    for (const row of w.rows) {
      if (row.type !== 'fixed') continue;
      for (const cell of row.cells) if (cell.widget) collectPresetCanonMaps(cell.widget, inModal, maps);
    }
  }
}

/** 2차 pass — 위젯을 canonical 구조(치환된 id/bindingKey/참조 + presetRef 제외)로 변환한다. */
function canonicalizePresetNode(w: ComposerWidget, inModal: boolean, maps: PresetCanonMaps): unknown {
  const mapKey = (v: string): string => maps.keyMap.get(v) ?? v;
  const mapId = (v: string): string => maps.idMap.get(v) ?? v;
  const out: Record<string, unknown> = { ...w };
  delete out.presetRef; // 출처 메타는 구조 아님 — 루트 스탬프 유무를 판정에서 배제
  out.id = mapId(w.id);
  if (w.kind === 'input' || w.kind === 'modalTrigger') {
    out.bindingKey = inModal ? w.bindingKey : mapKey(w.bindingKey);
  }
  if (!inModal) {
    if (typeof w.visibleWhen === 'string') out.visibleWhen = mapKey(w.visibleWhen);
    if (typeof w.enabledWhen === 'string') out.enabledWhen = mapKey(w.enabledWhen);
  }
  if (w.kind === 'input') {
    if (!inModal) {
      if (typeof w.labelSuffixFrom === 'string') out.labelSuffixFrom = mapKey(w.labelSuffixFrom);
      if (w.writes) out.writes = w.writes.map((wr) => ({ ...wr, target: mapKey(wr.target) }));
    }
    // actionItem.opensWidgetId는 위젯 id 축이라 모달 스코프 안에서도 재매핑(clone과 동일).
    if (w.actionItem) out.actionItem = { ...w.actionItem, opensWidgetId: mapId(w.actionItem.opensWidgetId) };
  }
  if (w.kind === 'group') {
    out.children = w.children.map((c) => canonicalizePresetNode(c, inModal, maps));
  } else if (w.kind === 'repeat') {
    // 행 스코프 children — 키 보존 스코프(inModal=true)로 canonical 변환(collect pass와 동일 규칙).
    out.children = w.children.map((c) => canonicalizePresetNode(c, true, maps));
  } else if (w.kind === 'branch') {
    // 투명 컨테이너 — 슬롯 id 토큰 치환 + children은 inModal **그대로**(collect pass와 동일 규칙).
    out.branches = w.branches.map((slot) => ({
      ...slot,
      id: mapId(slot.id),
      children: slot.children.map((c) => canonicalizePresetNode(c, inModal, maps)),
    }));
  } else if (w.kind === 'modalTrigger') {
    if (!inModal) {
      if (w.commit) out.commit = w.commit.map((m) => ({ ...m, to: mapKey(m.to) }));
      if (w.purpose) {
        out.purpose = { ...w.purpose, commit: w.purpose.commit.map((m) => ({ ...m, to: mapKey(m.to) })) };
      }
    }
    out.children = w.children.map((c) => canonicalizePresetNode(c, true, maps));
  } else if (w.kind === 'formTable') {
    out.rows = w.rows.map((row) => {
      if (row.type !== 'fixed') return row;
      return {
        ...row,
        cells: row.cells.map((cell) => {
          const c: Record<string, unknown> = { ...cell };
          if (cell.widget) c.widget = canonicalizePresetNode(cell.widget, inModal, maps);
          if (!inModal && cell.compute) {
            c.compute = {
              ...cell.compute,
              inputs: cell.compute.inputs.map((inp) =>
                inp.binding
                  ? { ...inp, binding: { ...inp.binding, bindingKey: mapKey(inp.binding.bindingKey) } }
                  : inp,
              ),
            };
          }
          return c;
        }),
      };
    });
  }
  return out;
}

/** 키 순서 무관 안정 직렬화(canonical 비교 전용) — 객체 키 정렬 + undefined 프로퍼티 제외. */
function stablePresetStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return `[${v.map(stablePresetStringify).join(',')}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stablePresetStringify(obj[k])}`).join(',')}}`;
}

/**
 * 두 프리셋 서브트리가 clone 정규화(위젯 id 무시 · 본문 bindingKey 재발급 차이 정규화 · 내부 참조
 * 재매핑 상쇄 · presetRef 메타 제외) 기준으로 동등한지 판정한다. 배치 당시 버전 스냅샷과 현재 인스턴스
 * 서브트리를 비교해 **동등=로컬 수정 없음**(즉시 버전 교체 가능), **다름=수정 있음**(교체 전 경고).
 * 순수 함수(입력 불변). 삽입 UI(EditorPage)는 이 함수 하나로 판정하고 자체 deep-equal을 재구현하지 않는다.
 */
export function presetSubtreeEquivalent(a: ComposerWidget, b: ComposerWidget): boolean {
  const canon = (root: ComposerWidget): string => {
    const maps: PresetCanonMaps = { idMap: new Map(), keyMap: new Map() };
    collectPresetCanonMaps(root, false, maps);
    return stablePresetStringify(canonicalizePresetNode(root, false, maps));
  };
  return canon(a) === canon(b);
}

/**
 * 트리 형제 배열(root/group children)에서 id 위젯과 그 형제 배열 내 위치를 찾는다.
 * formTable 셀 위젯은 형제가 아니라 대상에서 제외(찾지 못하면 null → caller no-op).
 */
function findWidgetSiblingLocation(
  tree: ComposerWidget[],
  id: string,
): { widget: ComposerWidget; parentId: string | null; index: number } | null {
  const search = (
    nodes: ComposerWidget[],
    parentId: string | null,
  ): { widget: ComposerWidget; parentId: string | null; index: number } | null => {
    const idx = nodes.findIndex((w) => w.id === id);
    if (idx >= 0) return { widget: nodes[idx], parentId, index: idx };
    for (const w of nodes) {
      if (isChildrenContainer(w)) {
        const found = search(w.children, w.id);
        if (found) return found;
      } else if (w.kind === 'branch') {
        // 슬롯 children도 형제 배열 — 그 안 위젯의 부모는 **슬롯 id**.
        for (const slot of w.branches) {
          const found = search(slot.children, slot.id);
          if (found) return found;
        }
      }
    }
    return null;
  };
  return search(tree, null);
}

/**
 * 트리 소유 위젯(design/group/formTable/treeOwned·registered input)을 딥 클론해 **원본 바로 다음
 * 형제**(같은 부모)로 삽입한다. 대상이 body 바인딩 파생 input이면(복제 시 bindingKey 중복) null 반환(no-op).
 * group/formTable 재귀 클론 시 파생 input 자식은 제외하고 트리 소유 input은 bindingKey를 유니크 재발급한다.
 * @param taken 이미 사용 중인 bindingKey 집합(트리+미배치+양식 — caller가 수집). 클론 키를 여기서 누적.
 * @param newId 위젯 id 생성기(테스트 결정성 위해 주입).
 * @returns 삽입된 트리 + 새 최상위 위젯 id, 또는 null(대상 미발견/파생 input).
 */
export function duplicateComposerWidget(
  tree: ComposerWidget[],
  id: string,
  taken: Set<string>,
  newId: () => string,
): { tree: ComposerWidget[]; newId: string } | null {
  const loc = findWidgetSiblingLocation(tree, id);
  if (!loc) return null;
  if (loc.widget.kind === 'input' && isDerivedInput(loc.widget)) return null;
  const clone = cloneWidgetDeep(loc.widget, taken, newId);
  const next = insertWidgetAt(tree, clone, loc.parentId, loc.index + 1);
  return { tree: next, newId: clone.id };
}

/**
 * 복수 위젯 복제(Ctrl+D 확장) — **블록 복제**(2026-07-14 사용자 확정): 선택 묶음을 부모별로 묶어
 * 각 부모 안 **마지막 원본 바로 다음**에 트리 순서대로 한 벌 삽입(1,2,3 → 1,2,3,1',2',3').
 * 인터리브(원본 옆 개별 삽입)가 아니라 Ctrl+C→Ctrl+V 결과와 동형이 되게 한다. 조상-자손 동시
 * 선택은 caller(선택 단계)가 배제. 파생 input/미발견 id는 스킵(no-op). taken은 클론 bindingKey를
 * 누적해 대상 간 '-copy' 충돌을 막는다.
 * @returns 갱신 트리 + 새 최상위 위젯 id 목록(부모별 블록 순서, 스킵된 id는 제외).
 */
export function duplicateComposerWidgets(
  tree: ComposerWidget[],
  ids: string[],
  taken: Set<string>,
  newId: () => string,
): { tree: ComposerWidget[]; newIds: string[] } {
  const idSet = new Set(ids);
  // 부모별 그룹핑(트리 표시 순서) — 파생 input은 duplicateComposerWidget과 동일하게 제외.
  const byParent = new Map<string | null, ComposerWidget[]>();
  const orderedParents: (string | null)[] = [];
  const collect = (nodes: ComposerWidget[], parentId: string | null): void => {
    for (const w of nodes) {
      if (idSet.has(w.id) && !(w.kind === 'input' && isDerivedInput(w))) {
        if (!byParent.has(parentId)) {
          byParent.set(parentId, []);
          orderedParents.push(parentId);
        }
        byParent.get(parentId)!.push(w);
      }
      // group·modalTrigger·repeat 모두 컨테이너 — 모달/행 템플릿 안 위젯 복제(Ctrl+D)도 대상으로 수집.
      if (isChildrenContainer(w)) collect(w.children, w.id);
      // branch 슬롯 안 위젯도 수집(parentId=슬롯 id — 블록 복제가 같은 슬롯 안에 삽입).
      else if (w.kind === 'branch') for (const slot of w.branches) collect(slot.children, slot.id);
    }
  };
  collect(tree, null);
  let cur = tree;
  const newIds: string[] = [];
  for (const parentId of orderedParents) {
    const originals = byParent.get(parentId)!; // 트리 순서 보장(collect가 순서대로 수집)
    const clones = originals.map((w) => cloneWidgetDeep(w, taken, newId));
    // 삽입 위치는 삽입 시점 기준으로 마지막 원본을 재조회(타 부모 삽입에 의한 시프트 방어 —
    // 같은 형제 배열은 이 루프에서 한 번만 삽입하므로 블록 내부 시프트는 insertAt 증분으로 처리).
    const loc = findWidgetSiblingLocation(cur, originals[originals.length - 1].id);
    if (!loc) continue;
    let insertAt = loc.index + 1;
    for (const c of clones) {
      cur = insertWidgetAt(cur, c, parentId, insertAt);
      insertAt += 1;
      newIds.push(c.id);
    }
  }
  return { tree: cur, newIds };
}

/**
 * 위젯 목록을 붙여넣기용으로 딥 클론(Ctrl+V) — 각 위젯 id를 재발급하고 트리 소유 input의 bindingKey를
 * '-copy' 유니크로 재발급한다(cloneWidgetDeep 재사용, taken 누적). **최상위** 파생(비-트리소유) input은
 * 제외(bindingKey 중복 방지 — duplicateComposerWidget의 top-level 파생 null 규약과 동형). group/formTable
 * 내부의 파생 자식·파생 셀 위젯은 cloneWidgetDeep이 규약대로 스킵/스트립한다. 클립보드 원본(widgets)은
 * 불변이라 같은 스냅샷으로 반복 붙여넣기해도 매번 새 id/키가 배정된다(taken 누적으로 상호 충돌 없음).
 */
export function cloneWidgetsForInsert(
  widgets: ComposerWidget[],
  taken: Set<string>,
  newId: () => string,
): ComposerWidget[] {
  const out: ComposerWidget[] = [];
  for (const w of widgets) {
    if (w.kind === 'input' && isDerivedInput(w)) continue; // 최상위 파생 input 제외
    out.push(cloneWidgetDeep(w, taken, newId));
  }
  return out;
}

/**
 * 위젯 목록을 anchorId 바로 다음(같은 부모 형제)에 입력 순서대로 삽입한다. 붙여넣기의 "선택 위젯 다음"
 * 배치 규칙 공용. anchorId를 트리에서 못 찾으면 위젯 유실 방지로 root 말미에 순서대로 append한다.
 */
export function insertWidgetsAfterId(
  tree: ComposerWidget[],
  anchorId: string,
  widgets: ComposerWidget[],
): ComposerWidget[] {
  if (widgets.length === 0) return tree;
  const loc = findWidgetSiblingLocation(tree, anchorId);
  if (!loc) {
    return widgets.reduce(
      (t, w) => insertWidgetAt(t, w, null, Number.MAX_SAFE_INTEGER),
      tree,
    );
  }
  let next = tree;
  widgets.forEach((w, i) => {
    next = insertWidgetAt(next, w, loc.parentId, loc.index + 1 + i);
  });
  return next;
}

/**
 * 트리에서 id 위젯을 updater 결과로 치환. group children 재귀 + formTable 고정 셀 위젯(중첩 InputWidget)
 * 하강. 셀 위젯은 캔버스에서 선택돼 속성 패널로 편집되므로(2a selectedWidgetById 하강) 여기서도
 * 셀까지 찾아야 편집이 영속된다. 셀 위젯은 항상 InputWidget이라 updater 결과가 input이 아니면 무시(방어).
 * 미발견 시 무변경.
 */
export function updateWidget(
  tree: ComposerWidget[],
  id: string,
  updater: (w: ComposerWidget) => ComposerWidget,
): ComposerWidget[] {
  let changed = false;
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] => {
    const out = nodes.map((w) => {
      if (w.id === id) {
        changed = true;
        return updater(w);
      }
      if (isChildrenContainer(w)) {
        const children = walk(w.children);
        return children === w.children ? w : { ...w, children };
      }
      if (w.kind === 'branch') {
        // branch 위젯 자체 갱신(condition/label/visibleWhen 등)은 위 `w.id === id`가 처리.
        // 여기선 슬롯 children 하강 — 슬롯 안 위젯 속성 편집 영속.
        let slotsChanged = false;
        const branches = w.branches.map((slot) => {
          const children = walk(slot.children);
          if (children === slot.children) return slot;
          slotsChanged = true;
          return { ...slot, children };
        });
        return slotsChanged ? { ...w, branches } : w;
      }
      if (w.kind === 'formTable') {
        let rowsChanged = false;
        const rows = w.rows.map((row) => {
          if (row.type !== 'fixed') return row;
          let cellsChanged = false;
          const cells = row.cells.map((cell) => {
            if (!cell.widget || cell.widget.id !== id) return cell;
            const updated = updater(cell.widget);
            if (updated.kind !== 'input') return cell; // 셀 위젯은 input 전용
            changed = true;
            cellsChanged = true;
            return { ...cell, widget: updated };
          });
          if (cellsChanged) rowsChanged = true;
          return cellsChanged ? { ...row, cells } : row;
        });
        return rowsChanged ? { ...w, rows } : w;
      }
      return w;
    });
    return out;
  };
  const next = walk(tree);
  return changed ? next : tree;
}

/** 위젯 id의 부모 group id를 반환. root 직속이면 null, 트리에 없으면 undefined(미발견). */
export function widgetParentId(
  tree: ComposerWidget[],
  id: string,
): string | null | undefined {
  const walk = (nodes: ComposerWidget[], parent: string | null): string | null | undefined => {
    for (const w of nodes) {
      if (w.id === id) return parent;
      if (isChildrenContainer(w)) {
        const found = walk(w.children, w.id);
        if (found !== undefined) return found;
      } else if (w.kind === 'branch') {
        // 슬롯 안 위젯의 부모는 **슬롯 id**(트리 순회 부모 키). 슬롯 id 자체를 물으면 부모는
        // branch 위젯 id — 조상 체인이 슬롯에서 끊기지 않고 branch→상위로 이어진다
        // (2026-07-21 캔버스 그룹 우선 선택이 슬롯 안 위젯에서 무산되던 결함의 rc측 수정).
        for (const slot of w.branches) {
          if (slot.id === id) return w.id;
          const found = walk(slot.children, slot.id);
          if (found !== undefined) return found;
        }
      }
    }
    return undefined;
  };
  return walk(tree, null);
}

/**
 * 같은 부모의 형제 위젯들(ids)을 새 섹션(group)으로 묶는다. 형제가 아니면(부모 상이/미발견/2개 미만)
 * 트리 무변 + groupId=null. 새 group을 **첫 선택 위치**(형제 배열 내 최소 인덱스)에 삽입하고,
 * 선택 위젯들을 **형제 배열 순서대로** 그 children으로 이동한다(선택 순서 무관, 문서 순서 유지).
 */
export function groupWidgets(
  tree: ComposerWidget[],
  ids: string[],
  newGroupId: string,
): { tree: ComposerWidget[]; groupId: string | null } {
  const idSet = new Set(ids);
  // 단일 위젯도 허용(2026-07-16 사용자 확정: 한 개 선택 + Ctrl+G = 새 섹션으로 감싸 들어가기).
  if (idSet.size < 1) return { tree, groupId: null };
  const parent = widgetParentId(tree, [...idSet][0]);
  if (parent === undefined) return { tree, groupId: null };
  for (const id of idSet) {
    if (widgetParentId(tree, id) !== parent) return { tree, groupId: null };
  }
  const transform = (siblings: ComposerWidget[]): ComposerWidget[] => {
    const selectedInOrder = siblings.filter((w) => idSet.has(w.id));
    if (selectedInOrder.length !== idSet.size) return siblings; // 안전장치(형제 배열 밖 id 혼입)
    const group = createGroupWidget(newGroupId, { children: selectedInOrder });
    const out: ComposerWidget[] = [];
    let inserted = false;
    for (const w of siblings) {
      if (idSet.has(w.id)) {
        if (!inserted) {
          out.push(group); // 첫 선택 위치에 새 섹션 삽입
          inserted = true;
        }
        continue; // 선택 위젯은 group children으로 이동(형제 배열에서 제거)
      }
      out.push(w);
    }
    return out;
  };
  // 형제 배열(root / 섹션·모달·반복 행 템플릿 children / branch 슬롯 children) 안에서 묶기.
  // applyToSiblingsOf가 슬롯 children까지 형제 배열로 취급하므로 slot-id 부모도 동작한다.
  const next = applyToSiblingsOf(tree, [...idSet][0], transform);
  return { tree: next, groupId: newGroupId };
}

/**
 * group 위젯(groupId)을 해제 — 그 자리에서 children을 부모 형제 배열로 순서대로 풀어놓는다.
 * groupId가 group이 아니거나 미발견이면 무변경.
 */
export function ungroupWidget(tree: ComposerWidget[], groupId: string): ComposerWidget[] {
  const transform = (siblings: ComposerWidget[]): ComposerWidget[] => {
    const idx = siblings.findIndex((w) => w.id === groupId);
    if (idx < 0) return siblings;
    const g = siblings[idx];
    if (g.kind !== 'group') return siblings;
    return [...siblings.slice(0, idx), ...g.children, ...siblings.slice(idx + 1)];
  };
  const parent = widgetParentId(tree, groupId);
  if (parent === undefined) return tree;
  // 형제 배열(root / 컨테이너 children / branch 슬롯 children) 안에서 해제 — applyToSiblingsOf가
  // 슬롯 children까지 형제 배열로 취급하므로 slot-id 부모도 동작.
  return applyToSiblingsOf(tree, groupId, transform);
}

// ─────────────────────────────────────────────────────────────
// 분기 위젯 슬롯 ops (branch) — BranchWidget.branches 슬롯 추가/삭제/이동/조건·라벨 편집 (모두 불변)
// 본문 templateOps의 branch 슬롯 ops(addBranchSlot/removeBranchSlot/moveBranchSlot/…) 미러.
// 슬롯 내부 children(위젯 배열)의 추가/삭제/이동/편집은 root/group.children와 동일하게 범용 ops
// (insertWidgetAt(parentId=슬롯 id)/removeWidgetById/moveWidgetInSiblings/updateWidget)가 담당한다.
// ─────────────────────────────────────────────────────────────

/** branchId인 branch 위젯을 트리에서 찾아 transform을 적용한 새 트리를 반환(불변, 중첩 재귀). */
function mapBranchWidget(
  tree: ComposerWidget[],
  branchId: string,
  transform: (branch: BranchWidget) => BranchWidget,
): ComposerWidget[] {
  let changed = false;
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] => {
    const out = nodes.map((w) => {
      if (w.id === branchId && w.kind === 'branch') {
        const next = transform(w);
        if (next !== w) changed = true;
        return next;
      }
      // 중첩(group/modalTrigger/repeat children · 타 branch 슬롯)에 branch가 있을 수 있어 재귀.
      if (isChildrenContainer(w)) {
        const children = walk(w.children);
        return children === w.children ? w : { ...w, children };
      }
      if (w.kind === 'branch') {
        let slotsChanged = false;
        const branches = w.branches.map((slot) => {
          const children = walk(slot.children);
          if (children === slot.children) return slot;
          slotsChanged = true;
          return { ...slot, children };
        });
        return slotsChanged ? { ...w, branches } : w;
      }
      return w;
    });
    return changed ? out : nodes;
  };
  const next = walk(tree);
  return changed ? next : tree;
}

/** branch 슬롯 id 충돌 회피 — `${branch.id}-slot-${n}` (본문 genBranchSlotId 미러). */
function genBranchWidgetSlotId(branch: BranchWidget): string {
  const existing = new Set(branch.branches.map((s) => s.id));
  let n = branch.branches.length;
  let id = `${branch.id}-slot-${n}`;
  while (existing.has(id)) {
    n += 1;
    id = `${branch.id}-slot-${n}`;
  }
  return id;
}

/**
 * branchId branch에 빈 슬롯(condition '' 빈 문자열, children [])을 index 위치에 추가한다(불변).
 * index 미지정/범위 밖이면 끝에 append. branchId가 branch가 아니면 무변경(참조 동일).
 */
export function addBranchWidgetSlot(
  tree: ComposerWidget[],
  branchId: string,
  index?: number,
): ComposerWidget[] {
  return mapBranchWidget(tree, branchId, (branch) => {
    const slot: BranchWidgetSlot = { id: genBranchWidgetSlotId(branch), condition: '', children: [] };
    const next = branch.branches.slice();
    if (index === undefined || index < 0 || index > next.length) next.push(slot);
    else next.splice(index, 0, slot);
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯을 제거한다(불변). 슬롯 제거 = 그 children 폐기(group 삭제 동형 —
 * 파생 input은 placed 집합 재계산으로 풀 자동 복귀). 마지막 1개만 남으면 거부(branch는 슬롯 ≥1 유지).
 * slotId 부재면 무변경(참조 동일).
 */
export function removeBranchWidgetSlot(
  tree: ComposerWidget[],
  branchId: string,
  slotId: string,
): ComposerWidget[] {
  return mapBranchWidget(tree, branchId, (branch) => {
    if (branch.branches.length <= 1) return branch; // 최소 1 가드
    const next = branch.branches.filter((s) => s.id !== slotId);
    if (next.length === branch.branches.length) return branch; // slotId 부재 → 무변경
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯을 한 칸 위(up)/아래(down)로 이동한다(불변).
 * 경계(첫 up/마지막 down)거나 slotId 부재면 무변경(참조 동일).
 */
export function moveBranchWidgetSlot(
  tree: ComposerWidget[],
  branchId: string,
  slotId: string,
  dir: 'up' | 'down',
): ComposerWidget[] {
  return mapBranchWidget(tree, branchId, (branch) => {
    const idx = branch.branches.findIndex((s) => s.id === slotId);
    if (idx === -1) return branch;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= branch.branches.length) return branch; // 경계 무변경
    const next = branch.branches.slice();
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯의 label/condition을 교체한다(불변, 부분 patch).
 * patch에 명시된(=undefined 아닌) 필드만 갱신하고, **빈 문자열은 허용**한다(condition ''=무조건
 * 매칭/else, label ''=미지정 취급). 값 무변경이면 참조 유지(무의미 undo 스텝 방지).
 * slotId 부재/branch 아님이면 무변경(참조 동일).
 */
export function updateBranchWidgetSlot(
  tree: ComposerWidget[],
  branchId: string,
  slotId: string,
  patch: { label?: string; condition?: string },
): ComposerWidget[] {
  return mapBranchWidget(tree, branchId, (branch) => {
    const idx = branch.branches.findIndex((s) => s.id === slotId);
    if (idx === -1) return branch;
    const cur = branch.branches[idx];
    const nextLabel = patch.label !== undefined ? patch.label : cur.label;
    const nextCondition = patch.condition !== undefined ? patch.condition : cur.condition;
    if (nextLabel === cur.label && nextCondition === cur.condition) return branch; // 무변경
    const next = branch.branches.map((s) => {
      if (s.id !== slotId) return s;
      const updated: BranchWidgetSlot = { ...s };
      if (patch.label !== undefined) updated.label = patch.label;
      if (patch.condition !== undefined) updated.condition = patch.condition;
      return updated;
    });
    return { ...branch, branches: next };
  });
}

/**
 * 분기 위젯 생성(팔레트 팩토리) — 슬롯 2개(각 condition '' 빈 문자열, children []). 본문 branch
 * 노드 팩토리(templateOps createDefaultNode('branch')) 대응. 투명 컨테이너라 자체 layout/스코프 없음.
 */
export function createBranchWidget(id: string): BranchWidget {
  return {
    kind: 'branch',
    id,
    branches: [
      { id: `${id}-slot-0`, condition: '', children: [] },
      { id: `${id}-slot-1`, condition: '', children: [] },
    ],
  };
}

/** 빈 그룹 위젯 생성(제목은 자식 heading DesignWidget으로 — GroupWidget은 그룹화만). */
export function createGroupWidget(
  id: string,
  opts?: { columns?: number; children?: ComposerWidget[] },
): GroupWidget {
  return {
    kind: 'group',
    id,
    ...(opts?.columns ? { columns: opts.columns } : {}),
    children: opts?.children ?? [],
  };
}

/** 디자인 위젯 생성(separator/heading/spacer). heading은 text가 섹션 제목. */
export function createDesignWidget(
  id: string,
  kind: 'separator' | 'heading' | 'spacer',
  text?: string,
): DesignWidget {
  return { kind, id, ...(text !== undefined ? { text } : {}) };
}

/**
 * 등록형 폼 위젯 생성 — synthetic bindingKey(`widget.<widgetKey>.<id>`)로 선택 상태를 content에 저장.
 * 라벨은 카탈로그 meta.label(없으면 widgetKey). writes(전파 매핑)는 admin 속성 패널에서 저작.
 */
export function createRegisteredWidget(id: string, widgetKey: string): InputWidget {
  const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === widgetKey);
  return {
    kind: 'input',
    id,
    bindingKey: `widget.${widgetKey}.${id}`,
    widget: 'registered',
    widgetKey,
    label: meta?.label ?? widgetKey,
  };
}

/** 고정 셀 좌표(작성폼 formTable 복수/범위 선택 공용). */
export interface FtCellRef {
  rowId: string;
  columnId: string;
}

/**
 * formTable 고정 셀의 Shift+클릭 사각 범위 선택 — anchor~target 직사각 범위에 걸치는
 * *실제 존재하는* 고정 셀들을 반환한다(엑셀/스프레드시트 관례).
 *
 * - 행 범위: `widget.rows` 중 `type==='fixed'` 행만 대상(사이에 repeat 행이 있으면 건너뜀).
 *   고정행 배열 인덱스로 anchor↔target 사이를 포함한다.
 * - 열 범위: `widget.columns` 배열 인덱스로 anchor 열↔target 열 사이. colSpan 셀은 점유 구간
 *   `[start, start+colSpan-1]`이 열 범위와 교차하면 포함한다.
 * - 각 행에서 실제 존재하는 셀(`row.cells`의 columnId)만 수집 — 행마다 셀 구성이 다를 수 있어
 *   빈 슬롯은 제외한다.
 * - anchor는 결과 **마지막**에 둔다(ftFocusCell.cells 마지막=anchor 불변식 — 연속 Shift+클릭이
 *   항상 같은 anchor 기준으로 범위 재계산되고, 단일 셀 속성 뷰·복사 기준도 anchor 불변).
 * - anchor/target 행이 고정행이 아니거나 열이 부재하면(방어) target 단일 선택으로 폴백한다.
 */
export function computeFtCellRange(
  widget: FormTableWidget,
  anchor: FtCellRef,
  target: FtCellRef,
): FtCellRef[] {
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const fixedRows = widget.rows.filter(
    (r): r is Extract<FormTableRow, { type: 'fixed' }> => r.type === 'fixed',
  );
  const anchorRowIdx = fixedRows.findIndex((r) => r.id === anchor.rowId);
  const targetRowIdx = fixedRows.findIndex((r) => r.id === target.rowId);
  if (anchorRowIdx < 0 || targetRowIdx < 0) return [{ ...target }];

  // 셀의 열 점유 구간 [start, end] (colSpan 인지). 열 부재 시 null.
  const colSpanOf = (
    row: Extract<FormTableRow, { type: 'fixed' }>,
    columnId: string,
  ): { start: number; end: number } | null => {
    const start = colIndex.get(columnId);
    if (start === undefined) return null;
    const cell = row.cells.find((c) => c.columnId === columnId);
    return { start, end: start + Math.max(1, cell?.colSpan ?? 1) - 1 };
  };
  const aCol = colSpanOf(fixedRows[anchorRowIdx], anchor.columnId);
  const tCol = colSpanOf(fixedRows[targetRowIdx], target.columnId);
  if (!aCol || !tCol) return [{ ...target }];

  const r0 = Math.min(anchorRowIdx, targetRowIdx);
  const r1 = Math.max(anchorRowIdx, targetRowIdx);
  const c0 = Math.min(aCol.start, tCol.start);
  const c1 = Math.max(aCol.end, tCol.end);

  const rest: FtCellRef[] = [];
  for (let ri = r0; ri <= r1; ri++) {
    const row = fixedRows[ri];
    // 열 점유 맵(colIdx → 점유 셀 columnId, colSpan 커버 포함). 모델에 아직 미생성인 빈 그리드
    // 위치도 렌더는 columns 기준 td를 만들고 ftSetFixedCell이 upsert하므로, 범위는 row.cells가
    // 아니라 **그리드(columns)** 기준으로 수집한다(미생성 셀 범위 누락 버그 — 2026-07-13).
    const covered = new Map<number, string>();
    for (const cell of row.cells) {
      const start = colIndex.get(cell.columnId);
      if (start === undefined) continue; // orphan columnId(열 삭제 잔재)
      const end = start + Math.max(1, cell.colSpan ?? 1) - 1;
      for (let k = start; k <= end; k++) covered.set(k, cell.columnId);
    }
    const seen = new Set<string>();
    for (let ci = c0; ci <= c1 && ci < widget.columns.length; ci++) {
      const columnId = covered.get(ci) ?? widget.columns[ci].id;
      if (seen.has(columnId)) continue; // colSpan 점유 셀은 1회만
      seen.add(columnId);
      if (row.id === anchor.rowId && columnId === anchor.columnId) continue; // anchor는 마지막에
      rest.push({ rowId: row.id, columnId });
    }
  }
  return [...rest, { rowId: anchor.rowId, columnId: anchor.columnId }];
}

/** 블록 복사 슬롯 — 선택 bounding box top-left 기준 상대 오프셋(dr,dc) + 출처 셀 좌표(빈 그리드 위치는 null). */
export interface FtBlockSlot {
  dr: number;
  dc: number;
  ref: FtCellRef | null;
}

/** 블록 복사 레이아웃 — bounding box 크기(rows×cols) + 슬롯 목록. */
export interface FtBlockLayout {
  rows: number;
  cols: number;
  slots: FtBlockSlot[];
}

/** widget에서 고정 행 배열만 추출(repeat 행 제외 — 블록 좌표 계산 공용). */
function ftFixedRows(widget: FormTableWidget): Extract<FormTableRow, { type: 'fixed' }>[] {
  return widget.rows.filter(
    (r): r is Extract<FormTableRow, { type: 'fixed' }> => r.type === 'fixed',
  );
}

/**
 * formTable 고정 셀 블록 복사 레이아웃 수집 — 선택 cells의 bounding box(고정행 인덱스 × 열 인덱스,
 * repeat 행 제외)를 훑어 각 그리드 위치를 top-left 기준 상대 오프셋(dr,dc)과 함께 수집한다(스프레드시트 블록 복사).
 *
 * - box 안의 미생성(빈) 그리드 위치도 `ref: null` 슬롯으로 포함 — 블록 붙여넣기 시 대상을 비운다.
 * - colSpan 셀은 시작 위치 1회만 슬롯화(점유 연속 열은 스킵). box 밖에서 시작해 안으로 걸친 colSpan은
 *   그 걸친 열이 연속 열로 스킵돼 슬롯이 생기지 않는다(부분 겹침은 무접촉).
 * - repeat 행은 fixedRows에서 제외돼 box·오프셋 계산에 포함되지 않는다.
 * - cells에 고정행 셀 좌표가 하나도 없으면(전부 repeat/삭제열) null.
 */
export function collectFtBlockLayout(
  widget: FormTableWidget,
  cells: FtCellRef[],
): FtBlockLayout | null {
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const fixedRows = ftFixedRows(widget);
  const rowIdxOf = new Map(fixedRows.map((r, i) => [r.id, i]));
  const pts: { r: number; c: number }[] = [];
  for (const cell of cells) {
    const r = rowIdxOf.get(cell.rowId);
    const c = colIndex.get(cell.columnId);
    if (r === undefined || c === undefined) continue;
    pts.push({ r, c });
  }
  if (pts.length === 0) return null;
  const r0 = Math.min(...pts.map((p) => p.r));
  const r1 = Math.max(...pts.map((p) => p.r));
  const c0 = Math.min(...pts.map((p) => p.c));
  const c1 = Math.max(...pts.map((p) => p.c));

  const slots: FtBlockSlot[] = [];
  for (let ri = r0; ri <= r1; ri++) {
    const row = fixedRows[ri];
    // 이 행의 셀 시작 열(colIdx→columnId)과 colSpan 연속 열(스킵 대상) 분리.
    const startAt = new Map<number, string>();
    const contd = new Set<number>();
    for (const cell of row.cells) {
      const start = colIndex.get(cell.columnId);
      if (start === undefined) continue; // orphan columnId(열 삭제 잔재)
      startAt.set(start, cell.columnId);
      const end = start + Math.max(1, cell.colSpan ?? 1) - 1;
      for (let k = start + 1; k <= end; k++) contd.add(k);
    }
    for (let ci = c0; ci <= c1 && ci < widget.columns.length; ci++) {
      if (contd.has(ci)) continue; // colSpan 연속 열은 시작 위치에서만 수집
      const columnId = startAt.get(ci);
      slots.push({
        dr: ri - r0,
        dc: ci - c0,
        ref: columnId ? { rowId: row.id, columnId } : null,
      });
    }
  }
  return { rows: r1 - r0 + 1, cols: c1 - c0 + 1, slots };
}

/**
 * 선택 cells의 bounding box top-left 그리드 위치(고정행/열 인덱스) — 블록 붙여넣기 anchor 계산.
 * 행/열 최솟값을 독립으로 취한다(코너가 실제 선택 셀이 아니어도 무방). 고정행 셀이 없으면 null.
 */
export function ftSelectionTopLeft(
  widget: FormTableWidget,
  cells: FtCellRef[],
): { rowIdx: number; colIdx: number } | null {
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const rowIdxOf = new Map(ftFixedRows(widget).map((r, i) => [r.id, i]));
  let minR = Infinity;
  let minC = Infinity;
  for (const cell of cells) {
    const r = rowIdxOf.get(cell.rowId);
    const c = colIndex.get(cell.columnId);
    if (r === undefined || c === undefined) continue;
    if (r < minR) minR = r;
    if (c < minC) minC = c;
  }
  if (!Number.isFinite(minR) || !Number.isFinite(minC)) return null;
  return { rowIdx: minR, colIdx: minC };
}

/**
 * 블록 붙여넣기 대상 좌표 매핑 — anchor(top-left) 그리드 위치 + 슬롯 오프셋(dr,dc)을 그리드 좌표로
 * 환산해 대상 셀 좌표를 만든다. 그리드 밖(행/열 초과)인 슬롯은 제외한다(스프레드시트 클리핑).
 * repeat 행은 fixedRows에서 제외돼 자연히 스킵된다. 반환은 입력 slots 순서를 유지.
 */
export function ftBlockTargets(
  widget: FormTableWidget,
  anchor: { rowIdx: number; colIdx: number },
  slots: { dr: number; dc: number }[],
): { dr: number; dc: number; target: FtCellRef }[] {
  const fixedRows = ftFixedRows(widget);
  const out: { dr: number; dc: number; target: FtCellRef }[] = [];
  for (const { dr, dc } of slots) {
    const ri = anchor.rowIdx + dr;
    const ci = anchor.colIdx + dc;
    if (ri < 0 || ri >= fixedRows.length) continue;
    if (ci < 0 || ci >= widget.columns.length) continue;
    out.push({ dr, dc, target: { rowId: fixedRows[ri].id, columnId: widget.columns[ci].id } });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────
// formTable 행/열 캔버스 직접 조작 — 선택 셀 기준 인접 삽입·삭제 (parity P1, 2026-07-13).
// 열=스키마(칼럼 정의), 고정 셀은 columnId로 매칭(희소 그리드)이라 열 삽입은 행 파급이 없다.
// colSpan 셀이 조작 지점을 가로지르면 병합을 유지하도록 span을 ±1 조정한다(양식 탭 관례).
// ─────────────────────────────────────────────────────────────

/** 새 행/열 id 생성 — crypto.randomUUID 우선, 미지원(구형 런타임) 시 폴백. */
function ftGenId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID?.() ?? `ft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * anchor 행 기준 새 **고정 행**을 위/아래에 삽입한다. anchor가 repeat 행이어도 인접 위치에
 * 고정 행을 삽입한다(삽입 결과는 항상 고정 행 — 양식 탭 관례). anchor 미발견 시 무변경.
 * 양식 탭 addRow 동형(2026-07-14 경로 통일):
 *  - 삽입 지점을 세로로 가로지르는 rowSpan은 span+1 확장(병합 유지), 그 열 구간은 새 행에서
 *    커버(셀 미생성) — 미확장 시 새 행이 병합 영역에 흡수돼 위 셀이 이어져 보이는 오동작.
 *  - 커버 밖 각 열 슬롯엔 **빈 텍스트 셀(text:'')** + anchor(고정) 행 covering 셀의 표현 속성
 *    (style/정렬) 상속. 콘텐츠·종류(위젯/계산/자동 채움)·병합은 미상속. 행 높이 승계.
 * id는 caller가 넘길 수 있고(테스트 결정성), 생략 시 자동 생성.
 */
export function ftInsertRow(
  widget: FormTableWidget,
  anchorRowId: string,
  where: 'above' | 'below',
  newRowId: string = ftGenId(),
): FormTableWidget {
  const idx = widget.rows.findIndex((r) => r.id === anchorRowId);
  if (idx < 0) return widget;
  const at = where === 'above' ? idx : idx + 1;
  const colIdx = new Map(widget.columns.map((c, i) => [c.id, i]));
  // 삽입 지점(고정행 좌표)을 가로지르는 rowSpan 확장 + 커버 열 수집(양식 탭 rowSpanCrosses 동형).
  // 병합은 인접 고정 행 전용이라 고정행 인덱스 거리로 판정(ftRemoveRow와 동일 좌표계).
  const fixedIdxOf = new Map(ftFixedRows(widget).map((r, i) => [r.id, i]));
  let atFixed = 0;
  for (let i = 0; i < at; i++) if (widget.rows[i].type === 'fixed') atFixed++;
  const coveredCols = new Set<number>();
  const rows0 = widget.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed') return row;
    const myIdx = fixedIdxOf.get(row.id);
    if (myIdx === undefined || myIdx >= atFixed) return row;
    let changed = false;
    const cells = row.cells.map((cell) => {
      const rs = Math.max(1, cell.rowSpan ?? 1);
      if (rs <= 1) return cell;
      if (myIdx < atFixed && atFixed <= myIdx + rs - 1) {
        const start = colIdx.get(cell.columnId);
        if (start !== undefined) {
          const cs = Math.max(1, cell.colSpan ?? 1);
          for (let k = start; k < start + cs; k++) coveredCols.add(k);
        }
        changed = true;
        return { ...cell, rowSpan: rs + 1 };
      }
      return cell;
    });
    return changed ? { ...row, cells } : row;
  });
  // 새 행 셀 — 커버 밖 각 열 슬롯에 빈 텍스트 셀 + 인접(anchor 고정 행) 표현 속성 상속.
  const anchor = widget.rows[idx];
  const cells: FormTableFixedCell[] = [];
  let height: Extract<FormTableRow, { type: 'fixed' }>['height'];
  if (anchor.type === 'fixed') height = anchor.height;
  widget.columns.forEach((col, ci) => {
    if (coveredCols.has(ci)) return;
    let props: Partial<FormTableFixedCell> | null = null;
    if (anchor.type === 'fixed') {
      const src = anchor.cells.find((cell) => {
        const start = colIdx.get(cell.columnId);
        if (start === undefined) return false;
        const span = Math.max(1, cell.colSpan ?? 1);
        return start <= ci && ci < start + span;
      });
      props = src ? ftInheritedCellProps(src) : null;
    }
    cells.push({ columnId: col.id, text: '', ...(props ?? {}) });
  });
  const newRow: FormTableRow = {
    type: 'fixed',
    id: newRowId,
    cells,
    ...(height != null ? { height } : {}),
  };
  return { ...widget, rows: [...rows0.slice(0, at), newRow, ...rows0.slice(at)] };
}

/**
 * 새 행/열 셀이 인접 셀에서 상속하는 표현 속성(style 깊은 복사 + 정렬) — 양식 탭 addRow/addCol의
 * inheritCellProps 동형. 콘텐츠(text/widget/compute/prefillFrom)·병합(colSpan/rowSpan)은 미상속.
 * legacy align은 contentAlignH로 변환해 승계(신규 저장 금지 규약 — left는 기본 start라 생략).
 * 상속할 표현 속성이 전무하면 null(셀 미생성 신호).
 */
function ftInheritedCellProps(cell: FormTableFixedCell): Partial<FormTableFixedCell> | null {
  const out: Partial<FormTableFixedCell> = {};
  if (cell.style) {
    const style: TableCellStyle = { ...cell.style };
    if (cell.style.padding) style.padding = { ...cell.style.padding };
    out.style = style;
  }
  const alignH =
    cell.contentAlignH ??
    (cell.align === 'center' ? 'center' : cell.align === 'right' ? 'end' : undefined);
  if (alignH !== undefined) out.contentAlignH = alignH;
  if (cell.contentAlignV !== undefined) out.contentAlignV = cell.contentAlignV;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * 행 제거(고정/반복 모두). rowId 미발견 시 무변경. 삭제 행을 세로로 가로지르는(자신이 시작이 아닌)
 * rowSpan 셀은 상위 시작 행에서 span-1(1이면 rowSpan 키 제거) — colSpan 축소(ftRemoveColumn)의 대칭.
 * 병합은 인접 고정 행들로만 구성되므로 rowSpan은 고정행 인덱스 거리와 같다(계산은 고정행 인덱스 기준).
 */
export function ftRemoveRow(widget: FormTableWidget, rowId: string): FormTableWidget {
  const fixedRows = ftFixedRows(widget);
  const fixedIdxOf = new Map(fixedRows.map((r, i) => [r.id, i]));
  const remIdx = fixedIdxOf.get(rowId);
  const rows = widget.rows
    .filter((r) => r.id !== rowId)
    .map((row): FormTableRow => {
      if (row.type !== 'fixed' || remIdx === undefined) return row;
      const myIdx = fixedIdxOf.get(row.id);
      if (myIdx === undefined || myIdx >= remIdx) return row; // 삭제 행보다 위(시작 후보)만 대상
      const cells = row.cells.map((cell) => {
        const rs = Math.max(1, cell.rowSpan ?? 1);
        if (rs <= 1) return cell;
        const end = myIdx + rs - 1;
        if (myIdx < remIdx && remIdx <= end) {
          const next: FormTableFixedCell = { ...cell };
          const nextSpan = rs - 1;
          if (nextSpan >= 2) next.rowSpan = nextSpan;
          else delete next.rowSpan;
          return next;
        }
        return cell;
      });
      return { ...row, cells };
    });
  return { ...widget, rows };
}

/**
 * anchor 열 기준 새 열(구조 축만 — id, 라벨 없음)을 좌/우에 삽입한다. 기존 셀은 columnId로 매칭돼
 * 위치 파급이 없다(희소 그리드). 삽입 지점이 고정 셀의 colSpan 점유 구간 **내부**(start < 삽입점 <= end)면
 * span+1로 확장해 병합을 유지한다. anchor 미발견 시 무변경.
 * 새 열은 anchor 열 폭 + 각 고정 행에서 anchor 열 covering 셀의 표현 속성(style/정렬)을 상속
 * (콘텐츠 미상속 — ftInheritedCellProps 참조, 양식 탭 addCol 동형).
 * (헤더=일반 셀 모델(2026-07-13)이라 column.label 부여 금지 — 부여 시 정규화가 헤더 행을 재생성한다.)
 */
export function ftInsertColumn(
  widget: FormTableWidget,
  anchorColumnId: string,
  where: 'left' | 'right',
  newColumnId: string = ftGenId(),
): FormTableWidget {
  const anchorIdx = widget.columns.findIndex((c) => c.id === anchorColumnId);
  if (anchorIdx < 0) return widget;
  const insertIdx = where === 'left' ? anchorIdx : anchorIdx + 1;
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  // 새 열은 인접(anchor) 열의 폭을 승계(양식 탭 addCol colWidths 승계 동형, 2026-07-14).
  // deprecated 축(label/group/widthPct/align)은 복사하지 않는다(신규 저장 금지 — label 복사 시
  // normalize가 유령 헤더 셀을 파생하는 사고 방지).
  const anchorCol = widget.columns[anchorIdx];
  const newCol: FormTableColumn = { id: newColumnId };
  if (anchorCol.width !== undefined) newCol.width = anchorCol.width;
  const columns = [
    ...widget.columns.slice(0, insertIdx),
    newCol,
    ...widget.columns.slice(insertIdx),
  ];
  const rows = widget.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed') return row; // repeat: cellFields는 columnId 매핑이라 위치 무관
    let crossed = false; // 이 행에서 삽입 지점을 가로지르는 colSpan이 확장됐는지(새 위치가 커버됨)
    const cells = row.cells.map((cell) => {
      const start = colIndex.get(cell.columnId);
      if (start === undefined) return cell; // orphan columnId(열 삭제 잔재)
      const span = Math.max(1, cell.colSpan ?? 1);
      const end = start + span - 1;
      if (start < insertIdx && insertIdx <= end) {
        crossed = true;
        return { ...cell, colSpan: span + 1 };
      }
      return cell;
    });
    // 새 열 셀 — 커버 밖 행마다 빈 텍스트 셀(text:'') + 인접(anchor) 열 covering 셀의 표현 속성
    // 상속(양식 탭 addCol 동형). span이 삽입 지점을 가로질러 확장된 행은 새 위치가 커버돼 미생성.
    if (!crossed) {
      const src = row.cells.find((cell) => {
        const start = colIndex.get(cell.columnId);
        if (start === undefined) return false;
        const span = Math.max(1, cell.colSpan ?? 1);
        return start <= anchorIdx && anchorIdx < start + span;
      });
      const props = src ? ftInheritedCellProps(src) : null;
      cells.push({ columnId: newColumnId, text: '', ...(props ?? {}) });
    }
    return { ...row, cells };
  });
  return { ...widget, columns, rows };
}

/**
 * 열 제거 + 그 열을 참조하는 셀 정리. 고정 행: 그 columnId 셀 제거, 그 열을 가로지르는(자신이 아닌)
 * colSpan 셀은 span-1(1이 되면 colSpan 제거). 반복 행: cellFields에서 그 열 매핑 제거.
 * columnId 미발견이어도 안전(그 열 참조가 없어 무변경).
 */
export function ftRemoveColumn(widget: FormTableWidget, columnId: string): FormTableWidget {
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const remIdx = colIndex.get(columnId);
  const columns = widget.columns.filter((c) => c.id !== columnId);
  const rows = widget.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed') {
      return { ...row, cellFields: row.cellFields.filter((cf) => cf.columnId !== columnId) };
    }
    const cells: FormTableFixedCell[] = [];
    for (const cell of row.cells) {
      if (cell.columnId === columnId) continue; // 그 열 셀 제거
      const start = remIdx === undefined ? undefined : colIndex.get(cell.columnId);
      if (remIdx !== undefined && start !== undefined) {
        const span = Math.max(1, cell.colSpan ?? 1);
        const end = start + span - 1;
        if (start < remIdx && remIdx <= end) {
          // 삭제 열이 이 셀의 병합 구간을 가로지름 → span-1(1이면 colSpan 키 제거).
          const nextSpan = span - 1;
          const next: FormTableFixedCell = { ...cell };
          if (nextSpan >= 2) next.colSpan = nextSpan;
          else delete next.colSpan;
          cells.push(next);
          continue;
        }
      }
      cells.push(cell);
    }
    return { ...row, cells };
  });
  return { ...widget, columns, rows };
}

// ─────────────────────────────────────────────────────────────
// formTable 셀 병합(colSpan + rowSpan) — 선택 직사각 범위를 top-left 한 셀로 병합 (parity P2, 2026-07-13).
// 규칙: 선택이 같은 표 고정 행들의 **직사각 범위**를 정확히 덮고, 걸친 셀이 전부 1×1(기존 병합 없음)이며,
//       범위 고정 행들이 물리적으로 연속(사이에 repeat 행 없음)일 때만 병합. top-left 내용만 보존.
// ─────────────────────────────────────────────────────────────

/** 병합 계획 — 직사각 범위(고정행 인덱스 × 열 인덱스) + top-left 좌표. 불가 시 null. */
interface FtMergePlan {
  r0: number;
  r1: number;
  c0: number;
  c1: number;
  topLeftRowId: string;
  topLeftColumnId: string;
}

function ftMergePlan(widget: FormTableWidget, cells: FtCellRef[]): FtMergePlan | null {
  if (cells.length < 2) return null; // 1칸은 병합 대상 아님
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const fixedRows = ftFixedRows(widget);
  const fixedIdxOf = new Map(fixedRows.map((r, i) => [r.id, i]));
  const pts: { r: number; c: number; ref: FtCellRef }[] = [];
  for (const ref of cells) {
    const r = fixedIdxOf.get(ref.rowId); // repeat 행/미발견이면 undefined → 거부
    const c = colIndex.get(ref.columnId);
    if (r === undefined || c === undefined) return null;
    pts.push({ r, c, ref });
  }
  const r0 = Math.min(...pts.map((p) => p.r));
  const r1 = Math.max(...pts.map((p) => p.r));
  const c0 = Math.min(...pts.map((p) => p.c));
  const c1 = Math.max(...pts.map((p) => p.c));
  const boxRows = r1 - r0 + 1;
  const boxCols = c1 - c0 + 1;
  if (boxRows * boxCols < 2) return null;

  // 범위 고정 행들이 widget.rows에서 물리적으로 연속인지(사이에 repeat 행이 끼면 rowSpan 금지).
  const rowPos = new Map(widget.rows.map((r, i) => [r.id, i]));
  const posR0 = rowPos.get(fixedRows[r0].id);
  const posR1 = rowPos.get(fixedRows[r1].id);
  if (posR0 === undefined || posR1 === undefined || posR1 - posR0 !== r1 - r0) return null;

  // 행 표시 조건(visibleWhen)과 rowSpan 상호배타(결정 2026-08-17) — 세로 병합(행 2+개)이
  // visibleWhen 행을 가로지르면 거부. 병합 진입 경로 전부(버튼/선택 병합/블록)가 이 plan을
  // 거치므로 여기가 방향 ①(조건 행 가로지르는 rowSpan 금지)의 단일 차단 지점.
  if (r1 > r0 && fixedRows.slice(r0, r1 + 1).some((r) => r.visibleWhen !== undefined)) return null;

  // 직사각 범위를 정확히(각 그리드 위치 1회씩) 덮고, 걸친 실제 셀이 전부 1×1인지.
  const seen = new Set<string>();
  for (const { r, c, ref } of pts) {
    const key = `${r}:${c}`;
    if (seen.has(key)) return null; // 중복 좌표
    seen.add(key);
    const cell = fixedRows[r].cells.find((x) => x.columnId === ref.columnId);
    if (cell && (Math.max(1, cell.colSpan ?? 1) > 1 || Math.max(1, cell.rowSpan ?? 1) > 1)) {
      return null; // 기존 병합 셀 포함 → 거부
    }
  }
  if (seen.size !== boxRows * boxCols) return null; // 직사각을 꽉 채우지 못함(비연결/구멍)

  return {
    r0,
    r1,
    c0,
    c1,
    topLeftRowId: fixedRows[r0].id,
    topLeftColumnId: widget.columns[c0].id,
  };
}

/** 선택 cells가 병합 가능한 직사각 범위인지(버튼 게이트용). ftMergeCells와 동일 규칙. */
export function ftCanMergeCells(widget: FormTableWidget, cells: FtCellRef[]): boolean {
  return ftMergePlan(widget, cells) !== null;
}

/**
 * 선택 직사각 범위를 top-left 한 셀로 병합. 불가(비직사각/반복 행 포함/기존 병합 셀 포함/연속 아님)면 null.
 * top-left 셀에 colSpan/rowSpan 설정(1이면 키 없음), 나머지 위치의 셀은 제거. 내용은 top-left만 보존
 * (나머지에 내용이 있으면 소실 — 호출측이 confirm).
 */
export function ftMergeCells(widget: FormTableWidget, cells: FtCellRef[]): FormTableWidget | null {
  const plan = ftMergePlan(widget, cells);
  if (!plan) return null;
  const { r0, r1, c0, c1, topLeftRowId, topLeftColumnId } = plan;
  const colIndex = new Map(widget.columns.map((c, i) => [c.id, i]));
  const fixedRows = ftFixedRows(widget);
  const boxRowIds = new Set(fixedRows.slice(r0, r1 + 1).map((r) => r.id));
  const inBox = (columnId: string): boolean => {
    const ci = colIndex.get(columnId);
    return ci !== undefined && ci >= c0 && ci <= c1;
  };
  const existingTopLeft = fixedRows[r0].cells.find((c) => c.columnId === topLeftColumnId);
  const colSpanVal = c1 - c0 + 1;
  const rowSpanVal = r1 - r0 + 1;
  const merged: FormTableFixedCell = { ...(existingTopLeft ?? {}), columnId: topLeftColumnId };
  if (colSpanVal >= 2) merged.colSpan = colSpanVal;
  else delete merged.colSpan;
  if (rowSpanVal >= 2) merged.rowSpan = rowSpanVal;
  else delete merged.rowSpan;

  const rows = widget.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed' || !boxRowIds.has(row.id)) return row;
    const kept = row.cells.filter((cell) => !inBox(cell.columnId)); // 범위 내 셀 전부 제거
    return { ...row, cells: row.id === topLeftRowId ? [...kept, merged] : kept };
  });
  return { ...widget, rows };
}

/** 병합 해제 — 해당 셀의 colSpan/rowSpan 제거(점유였던 위치는 빈 그리드로 남음). 미발견/비병합이면 무변경. */
export function ftUnmergeCell(widget: FormTableWidget, ref: FtCellRef): FormTableWidget {
  const rows = widget.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed' || row.id !== ref.rowId) return row;
    const cells = row.cells.map((cell) => {
      if (cell.columnId !== ref.columnId) return cell;
      const next: FormTableFixedCell = { ...cell };
      delete next.colSpan;
      delete next.rowSpan;
      return next;
    });
    return { ...row, cells };
  });
  return { ...widget, rows };
}
